import React, { useState, useEffect, useRef } from "react";
import { useAuth, usePlans } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import TherapistProfile from "./TherapistProfile";
import { TherapistData, TherapistStatus } from "../types/therapist";
import CurrencyInput from "react-currency-input-field";
import ConfirmModal from "./ConfirmModal";
import { Plan, BillingType, UserType } from '../types/plan';
import { PlanService } from '../services/PlanService';
import { AppointmentStatus, SessionType } from '../types/appointment';
import Swal from 'sweetalert2';

import {
  Users,
  User,
  Calendar,
  Clock,
  Settings,
  Plus,
  AlertCircle,
  Shield,
  UserCheck,
  UserX,
  CreditCard,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Video,
  Save,
  FileText,
  Star,
  X,
  DollarSign,
  Edit,
  Trash2,
  Ticket,
  TrendingUp,
} from "lucide-react";
import { collection, query, where, getDocs, doc, updateDoc, getDoc, setDoc, onSnapshot, collectionGroup, increment, serverTimestamp, deleteDoc, orderBy } from "firebase/firestore";
import { SplitPayment, SplitPaymentStatus } from "../types/splitPayment";
import { SPLIT_PAYMENTS_COLLECTION, SPLIT_PAYMENT_STATUS_LABELS, SPLIT_PAYMENT_STATUS_COLORS } from "../constants/splitPayment";
import { db } from "../firebase";
import GoogleCalendarSettings from "./GoogleCalendarSettings";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { TextInput } from "flowbite-react";
import AppHeader from "./AppHeader";
import { getUserNameFromRoomId } from "../utils/roomUtils";
import { useUserParticipations } from "../hooks/useUserParticipations";
import ChatButton from "./Dashboard/ChatButton";
import Chat from "./Dashboard/Chat";
import ChatWidget from "./Dashboard/ChatWidget";
import { ParticipantRole } from "../types/chat";
import AppointmentsOverview from "./Admin/AppointmentsOverview";
import ActiveSubscriptionsPanel from "./ActiveSubscriptionsPanel";

// Tipos e constantes para PIX
type PixKeyType = "CPF" | "CNPJ" | "EMAIL" | "PHONE" | "EVP";

const PIX_KEY_TYPES = {
  CPF: "CPF",
  CNPJ: "CNPJ",
  EMAIL: "EMAIL",
  PHONE: "PHONE",
  EVP: "EVP",
} as const;

const PIX_KEY_LABELS: Record<PixKeyType, string> = {
  CPF: "CPF",
  CNPJ: "CNPJ",
  EMAIL: "E-mail",
  PHONE: "Telefone",
  EVP: "Chave Aleatória (EVP)",
};

const PIX_KEY_PLACEHOLDERS: Record<PixKeyType, string> = {
  CPF: "000.000.000-00",
  CNPJ: "00.000.000/0000-00",
  EMAIL: "seu@email.com",
  PHONE: "(00) 00000-0000",
  EVP: "Chave aleatória gerada pelo banco",
};

// Tipos locais do Dashboard
interface DashboardTherapist {
  uid: string;
  name: string;
  email: string;
  status: TherapistStatus;
  showInShowcase?: boolean;
  showcaseAutoManaged?: boolean;
  lastPaymentStatus?: string;
  showcaseUpdatedBy?: 'system' | 'admin';
  creditBalances?: {
    sessions: {
      constellation: number;
      participation: number;
    };
    hours: number;
  };
}

interface Appointment {
  id: string;
  appointmentId?: string;
  isNextSession?: boolean;
  pacienteId: string;
  terapeutaId: string;
  pacienteNome: string;
  terapeutaNome: string;
  dataSessao: any;
  status: string;
  horasConsumidas?: boolean;
  // Novos campos para constelações
  tipo?: "consulta" | "constelacao";
  maxObservadores?: number;
  observadores?: string[];
  observadoresNomes?: string[];
  // Campo para controlar visibilidade pública
  isPublic?: boolean;
  permiteParticipantes?: boolean;
  // Campo cache mantido pela Cloud Function
  participantCount?: number;
}

// Interface TherapistData importada de ../types/therapist

// Tipo para o conteúdo da home page
interface HomeContent {
  heroTitle: string;
  heroSubtitle: string;
  experienceTitle: string;
  experienceSubtitle: string;
  feature1Title: string;
  feature1Text: string;
  feature2Title: string;
  feature2Text: string;
  feature3Title: string;
  feature3Text: string;
  analysisTitle: string;
  analysisText: string;
  analysisTagline: string;
  benefitsTitle: string;
  benefit1Title: string;
  benefit1Text: string;
  benefit2Title: string;
  benefit2Text: string;
  benefit3Title: string;
  benefit3Text: string;
  benefit4Title: string;
  benefit4Text: string;
  benefit5Title: string;
  benefit5Text: string;
  benefit6Title: string;
  benefit6Text: string;
  faqTitle: string;
  faq1Question: string;
  faq1Answer: string;
  faq2Question: string;
  faq2Answer: string;
  faq3Question: string;
  faq3Answer: string;
  ctaTitle: string;
  ctaText: string;
  footerCopyright: string;
}

// Sub-componente: Editor de Conteúdo da Home
const HomeContentEditor: React.FC = () => {
  const [content, setContent] = useState<HomeContent>({
    heroTitle: "",
    heroSubtitle: "",
    experienceTitle: "",
    experienceSubtitle: "",
    feature1Title: "",
    feature1Text: "",
    feature2Title: "",
    feature2Text: "",
    feature3Title: "",
    feature3Text: "",
    analysisTitle: "",
    analysisText: "",
    analysisTagline: "",
    benefitsTitle: "",
    benefit1Title: "",
    benefit1Text: "",
    benefit2Title: "",
    benefit2Text: "",
    benefit3Title: "",
    benefit3Text: "",
    benefit4Title: "",
    benefit4Text: "",
    benefit5Title: "",
    benefit5Text: "",
    benefit6Title: "",
    benefit6Text: "",
    faqTitle: "",
    faq1Question: "",
    faq1Answer: "",
    faq2Question: "",
    faq2Answer: "",
    faq3Question: "",
    faq3Answer: "",
    ctaTitle: "",
    ctaText: "",
    footerCopyright: "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");

  // Buscar conteúdo atual do Firestore
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setIsLoading(true);
        const docRef = doc(db, "siteContent", "XK9cio21NQLwgjfDUpoI");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<HomeContent>;
          setContent((prevContent) => ({
            ...prevContent,
            ...data,
          }));
        }
      } catch (error) {
        console.error("Erro ao buscar conteúdo:", error);
        setSaveStatus("error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, []);

  const handleInputChange = (field: keyof HomeContent, value: string) => {
    setContent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveStatus("idle");

      const docRef = doc(db, "siteContent", "XK9cio21NQLwgjfDUpoI");
      // Converter o objeto content para um formato que o Firestore aceita
      await updateDoc(docRef, { ...content });

      setSaveStatus("success");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Erro ao salvar conteúdo:", error);
      setSaveStatus("error");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="ml-2">Carregando conteúdo...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status de salvamento */}
      {saveStatus === "success" && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800">Conteúdo atualizado com sucesso!</span>
          </div>
        </div>
      )}

      {saveStatus === "error" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">Erro ao salvar alterações. Tente novamente.</span>
          </div>
        </div>
      )}

      {/* Seção Hero */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Seção Principal (Hero)</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título Principal</label>
            <input
              type="text"
              value={content.heroTitle}
              onChange={(e) => handleInputChange("heroTitle", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subtítulo Principal</label>
            <textarea
              value={content.heroSubtitle}
              onChange={(e) => handleInputChange("heroSubtitle", e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Seção Experiência */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Seção Experiência Virtual</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título da Experiência</label>
            <input
              type="text"
              value={content.experienceTitle}
              onChange={(e) => handleInputChange("experienceTitle", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subtítulo da Experiência</label>
            <textarea
              value={content.experienceSubtitle}
              onChange={(e) => handleInputChange("experienceSubtitle", e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Características da Plataforma</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="space-y-4">
            <h4 className="font-medium text-gray-800">Característica 1</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
              <input
                type="text"
                value={content.feature1Title}
                onChange={(e) => handleInputChange("feature1Title", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
              <textarea
                value={content.feature1Text}
                onChange={(e) => handleInputChange("feature1Text", e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-gray-800">Característica 2</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
              <input
                type="text"
                value={content.feature2Title}
                onChange={(e) => handleInputChange("feature2Title", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
              <textarea
                value={content.feature2Text}
                onChange={(e) => handleInputChange("feature2Text", e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-gray-800">Característica 3</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
              <input
                type="text"
                value={content.feature3Title}
                onChange={(e) => handleInputChange("feature3Title", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
              <textarea
                value={content.feature3Text}
                onChange={(e) => handleInputChange("feature3Text", e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Seção Análise */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Seção Análise Profissional</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título da Análise</label>
            <input
              type="text"
              value={content.analysisTitle}
              onChange={(e) => handleInputChange("analysisTitle", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Texto da Análise</label>
            <textarea
              value={content.analysisText}
              onChange={(e) => handleInputChange("analysisText", e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tagline da Análise</label>
            <input
              type="text"
              value={content.analysisTagline}
              onChange={(e) => handleInputChange("analysisTagline", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Seção Benefícios */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Seção Benefícios</h3>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título dos Benefícios</label>
            <input
              type="text"
              value={content.benefitsTitle}
              onChange={(e) => handleInputChange("benefitsTitle", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3">Benefício {num}</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Título</label>
                    <input
                      type="text"
                      value={content[`benefit${num}Title` as keyof HomeContent]}
                      onChange={(e) => handleInputChange(`benefit${num}Title` as keyof HomeContent, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Descrição</label>
                    <textarea
                      value={content[`benefit${num}Text` as keyof HomeContent]}
                      onChange={(e) => handleInputChange(`benefit${num}Text` as keyof HomeContent, e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Seção FAQ */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Seção FAQ</h3>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título da FAQ</label>
            <input
              type="text"
              value={content.faqTitle}
              onChange={(e) => handleInputChange("faqTitle", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {[1, 2, 3].map((num) => (
            <div key={num} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-3">Pergunta {num}</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Pergunta</label>
                  <input
                    type="text"
                    value={content[`faq${num}Question` as keyof HomeContent]}
                    onChange={(e) => handleInputChange(`faq${num}Question` as keyof HomeContent, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Resposta</label>
                  <textarea
                    value={content[`faq${num}Answer` as keyof HomeContent]}
                    onChange={(e) => handleInputChange(`faq${num}Answer` as keyof HomeContent, e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Seção CTA */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Seção Call to Action</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título do CTA</label>
            <input
              type="text"
              value={content.ctaTitle}
              onChange={(e) => handleInputChange("ctaTitle", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Texto do CTA</label>
            <textarea
              value={content.ctaText}
              onChange={(e) => handleInputChange("ctaText", e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Footer</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Copyright</label>
          <input
            type="text"
            value={content.footerCopyright}
            onChange={(e) => handleInputChange("footerCopyright", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Botão de Salvar */}
      <div className="flex justify-center pt-6">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Salvar Todas as Alterações
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Sub-componente: Gerenciador de Planos
const PlansManager: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ planId: string | null; planName: string }>({ planId: null, planName: "" });
  const formRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<Plan>({
    name: "",
    value: 0,
    billingType: BillingType.SESSIONS,
    quantity: 1,
    userType: UserType.PATIENT,
    isRecurring: false,
    description: "",
    isFeatured: false,
    sessionLabel: "",
    features: [],
    therapistCommission: 0,
    platformFee: 100,
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      const plansQuery = query(collection(db, "plans"));
      const snapshot = await getDocs(plansQuery);
      let plansList: Plan[] = snapshot.docs.map((doc) => 
        PlanService.fromFirestore(doc.id, doc.data())
      );
      // Filtrar planos não deletados (soft-delete)
      plansList = plansList.filter(plan => !plan.deleted);
      // Ordenar por ordem alfabética (nome)
      plansList.sort((a, b) => a.name.localeCompare(b.name));
      setPlans(plansList);
    } catch (err) {
      console.error("Erro ao buscar planos:", err);
      setError("Erro ao carregar planos");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof Plan, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingPlan(null);
    setFormData({
      name: "",
      value: 0,
      billingType: BillingType.SESSIONS,
      quantity: 1,
      userType: UserType.PATIENT,
      isRecurring: false,
      description: "",
      isFeatured: false,
      sessionLabel: "",
      features: [],
      therapistCommission: 0,
      platformFee: 100,
    });
  };

  const handleEdit = (plan: Plan) => {
    setIsCreating(false);
    setEditingPlan(plan);
    // Garantir que features seja sempre um array
    setFormData({
      ...plan,
      features: plan.features || []
    });
    // Scroll suave para o formulário
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingPlan(null);
    setFormData({
      name: "",
      value: 0,
      billingType: BillingType.SESSIONS,
      quantity: 1,
      userType: UserType.PATIENT,
      isRecurring: false,
      description: "",
      isFeatured: false,
      sessionLabel: "",
      features: [],
      therapistCommission: 0,
      platformFee: 100,
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Garantir que o valor seja um número decimal válido
      const valueAsNumber = typeof formData.value === 'number' 
        ? formData.value 
        : parseFloat(String(formData.value).replace(',', '.'));

      // Validação obrigatória do campo Tipo de Sessão para planos de sessões
      if (
        formData.billingType === BillingType.SESSIONS &&
        (!formData.sessionLabel || formData.sessionLabel.trim() === "")
      ) {
        setError("O campo 'Tipo de Sessão' é obrigatório para planos do tipo 'Por Sessões'.");
        setIsSaving(false);
        return;
      }

      if (!formData.name || isNaN(valueAsNumber) || valueAsNumber <= 0 || formData.quantity <= 0) {
        setError("Preencha todos os campos obrigatórios com valores válidos");
        setIsSaving(false);
        return;
      }

      // Preparar dados com valor como número decimal
      const planData = {
        ...formData,
        value: Number(valueAsNumber.toFixed(2)), // Garante 2 casas decimais
      };

      if (editingPlan?.id) {
        // Atualizar plano existente
        const planRef = doc(db, "plans", editingPlan.id);
        await updateDoc(planRef, planData);
      } else {
        // Criar novo plano
        await setDoc(doc(collection(db, "plans")), planData);
      }

      await fetchPlans();
      handleCancel();
    } catch (err) {
      console.error("Erro ao salvar plano:", err);
      setError("Erro ao salvar plano");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (planId: string, planName: string) => {
    setDeleteModal({ planId, planName });
  };

  const confirmDelete = async () => {
    if (!deleteModal.planId) return;

    try {
      setError(null);
      // Soft-delete: marcar como deletado ao invés de remover
      await updateDoc(doc(db, "plans", deleteModal.planId), { deleted: true });
      await fetchPlans();
      setDeleteModal({ planId: null, planName: "" });
    } catch (err) {
      console.error("Erro ao excluir plano:", err);
      setError("Erro ao excluir plano");
      setDeleteModal({ planId: null, planName: "" });
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({ planId: null, planName: "" });
  };

  const getFloatValue = (value: string | number) => {
    if (typeof value === 'number') return value;
    return parseFloat((value || '0').replace(/\./g, '').replace(',', '.')) || 0;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="ml-2">Carregando planos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Botão Criar Novo Plano */}
      {!isCreating && !editingPlan && (
        <div className="flex justify-end">
          <button
            onClick={handleCreateNew}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Criar Novo Plano
          </button>
        </div>
      )}

      {/* Formulário de Criação/Edição */}
      {(isCreating || editingPlan) && (
        <div ref={formRef}>
        <div className="bg-white rounded-lg shadow-md p-6 border-2 border-blue-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {editingPlan ? "Editar Plano" : "Criar Novo Plano"}
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Nome do Plano */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Plano*</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Plano Básico"
              />
            </div>

            {/* Descrição */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
              <textarea
                value={formData.description || ""}
                onChange={(e) => handleInputChange("description", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Ideal para quem está começando a jornada de autoconhecimento"
                rows={3}
              />
            </div>

            {/* Valor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valor (R$)*</label>
              <CurrencyInput
                id="plan-value-input"
                name="value"
                placeholder="0,00"
                value={formData.value}
                decimalsLimit={2}
                decimalScale={2}
                allowDecimals={true}
                decimalSeparator=","
                groupSeparator="."
                prefix="R$ "
                allowNegativeValue={false}
                onValueChange={(value) => {
                  handleInputChange("value", value);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Tipo de Cobrança */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Cobrança*</label>
              <select
                value={formData.billingType}
                onChange={(e) => handleInputChange("billingType", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="sessions">Por Sessões</option>
                <option value="hours">Por Horas</option>
              </select>
            </div>

            {/* Quantidade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantidade de {PlanService.getBillingTypeLabel(formData.billingType, true)}*
              </label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => handleInputChange("quantity", parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1"
              />
            </div>

            {/* Tipo de Sessão - obrigatório para planos de sessões */}
            {formData.billingType === BillingType.SESSIONS && (
              <div >
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Sessão*</label>
                <select
                  value={formData.sessionLabel || ""}
                  onChange={e => handleInputChange("sessionLabel", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="" disabled>Selecione o tipo</option>
                  <option value={SessionType.CONSTELACAO}>{SessionType.CONSTELACAO}</option>
                  <option value={SessionType.PARTICIPACAO}>{SessionType.PARTICIPACAO}</option>
                </select>
              </div>
            )}

            {/* Tipo de Usuário */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Usuário*</label>
              <select
                value={formData.userType}
                onChange={(e) => handleInputChange("userType", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="patient">Paciente</option>
                <option value="therapist">Terapeuta</option>
              </select>
            </div>

            {/* Plano Recorrente */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isRecurring}
                  onChange={(e) => handleInputChange("isRecurring", e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700">Plano Recorrente</span>
              </label>
            </div>

            {/* Plano em Destaque */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isFeatured || false}
                  onChange={(e) => handleInputChange("isFeatured", e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700">Plano em Destaque</span>
              </label>
            </div>

            {/* Comissões - Apenas para Constelação de Pacientes */}
            {formData.billingType === BillingType.SESSIONS &&
             formData.sessionLabel === SessionType.CONSTELACAO &&
             formData.userType === UserType.PATIENT && (
              <>
                <div className="md:col-span-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">Configuração de Repasse</p>
                        <p className="text-xs text-blue-700 mt-1">
                          Configure o percentual que será repassado ao terapeuta quando um paciente agendar uma sessão.
                          Os percentuais devem somar 100%.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comissão do Terapeuta */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Repasse ao Terapeuta (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.therapistCommission ?? 0}
                    onChange={(e) => {
                      const therapistValue = Number(e.target.value);
                      if (therapistValue >= 0 && therapistValue <= 100) {
                        handleInputChange("therapistCommission", therapistValue);
                        handleInputChange("platformFee", 100 - therapistValue);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Percentual que será creditado ao terapeuta (padrão: 0%)
                  </p>
                </div>

                {/* Taxa da Plataforma */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Taxa da Plataforma (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.platformFee ?? 100}
                    onChange={(e) => {
                      const platformValue = Number(e.target.value);
                      if (platformValue >= 0 && platformValue <= 100) {
                        handleInputChange("platformFee", platformValue);
                        handleInputChange("therapistCommission", 100 - platformValue);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Percentual que ficará na plataforma (padrão: 100%)
                  </p>
                </div>

                {/* Resumo da Distribuição */}
                <div className="md:col-span-2">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Distribuição do Valor</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Valor total do plano:</span>
                        <p className="font-semibold text-gray-900">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(getFloatValue(formData.value) || 0)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Repasse ao terapeuta ({formData.therapistCommission ?? 0}%):</span>
                        <p className="font-semibold text-green-700">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((getFloatValue(formData.value) || 0) * ((formData.therapistCommission ?? 0) / 100))}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Taxa da plataforma ({formData.platformFee ?? 100}%):</span>
                        <p className="font-semibold text-blue-700">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((getFloatValue(formData.value) || 0) * ((formData.platformFee ?? 100) / 100))}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Diferenciais do Plano */}
          <div className="md:col-span-2 mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Diferenciais do Plano</label>
            <div className="space-y-2">
              {(formData.features || []).map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => {
                      const newFeatures = [...(formData.features || [])];
                      newFeatures[index] = e.target.value;
                      handleInputChange("features", newFeatures);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: Acesso ilimitado à plataforma"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newFeatures = (formData.features || []).filter((_, i) => i !== index);
                      handleInputChange("features", newFeatures);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remover tópico">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const newFeatures = [...(formData.features || []), ""];
                  handleInputChange("features", newFeatures);
                }}
                className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" />
                Adicionar Diferencial
              </button>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Salvar
                </>
              )}
            </button>
          </div>
        </div>
        </div>
      )}

      {/* Lista de Planos */}
      <div className="space-y-4">
        {plans.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nenhum plano cadastrado ainda</p>
          </div>
        ) : (
          plans.map((plan) => (
            <div key={plan.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 text-lg">{plan.name}</h3>
                  {plan.description && (
                    <p className="text-sm text-gray-500 italic mt-1">{plan.description}</p>
                  )}
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Valor:</span> R$ {plan.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Tipo:</span>{" "}
                      {PlanService.getBillingTypeLabel(plan.billingType, true)} ({plan.quantity}{" "}
                      {PlanService.getBillingTypeLabel(plan.billingType, false).toLowerCase()})
                      {plan.billingType === BillingType.SESSIONS && plan.sessionLabel && (
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded ${plan.sessionLabel === SessionType.CONSTELACAO ? 'text-purple-700 bg-purple-100' : plan.sessionLabel === SessionType.PARTICIPACAO ? 'text-blue-700 bg-blue-100' : 'text-gray-700 bg-gray-100'}`}>para {plan.sessionLabel}</span>
                      )}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Usuário:</span>{" "}
                      {PlanService.getUserTypeLabel(plan.userType)}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Recorrente:</span> {plan.isRecurring ? "Sim" : "Não"}
                    </p>
                    {plan.features && plan.features.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm font-medium text-gray-700">Diferenciais:</span>
                        <ul className="mt-1 space-y-1">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-start gap-1">
                              <span className="text-blue-600 mt-0.5">•</span>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(plan)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => plan.id && handleDelete(plan.id, plan.name)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Excluir">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de confirmação de exclusão */}
      <ConfirmModal
        isOpen={!!deleteModal.planId}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Confirmar exclusão"
        description="Esta ação não pode ser desfeita."
        confirmText="Excluir plano"
        cancelText="Cancelar"
        variant="danger"
      >
        <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600" />
          <div>
            <p className="text-sm font-medium text-yellow-900">Você está prestes a excluir o plano:</p>
            <p className="mt-1 text-sm font-semibold text-yellow-800">"{deleteModal.planName}"</p>
          </div>
        </div>
      </ConfirmModal>
    </div>
  );
};

// Interface para Cupom
interface Coupon {
  id: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  expirationDate: string;
  usageLimit: number;
  usedCount: number;
  status: 'active' | 'inactive';
  createdAt: any;
  updatedAt: any;
}

// Interface para Cupom
interface Coupon {
  id: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  expirationDate: string;
  usageLimit: number;
  usedCount: number;
  status: 'active' | 'inactive';
  createdAt: any;
  updatedAt: any;
}

// Sub-componente: Painel do Administrador
const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"therapists" | "content" | "plans" | "splitPayments" | "coupons" | "appointments" | "manutencao">("therapists");
  const [userManagementTab, setUserManagementTab] = useState<"therapists" | "users">("therapists");
  const [therapists, setTherapists] = useState<DashboardTherapist[]>([]);
  const [regularUsers, setRegularUsers] = useState<any[]>([]);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creditModal, setCreditModal] = useState<{
    therapistId: string | null;
    therapistName: string;
    isTherapist: boolean;
    currentCredits: {
      hours: number;
      constellationSessions: number;
      participationSessions: number;
    };
  }>({ therapistId: null, therapistName: "", isTherapist: true, currentCredits: { hours: 0, constellationSessions: 0, participationSessions: 0 } });
  const [creditFormData, setCreditFormData] = useState({
    hours: 0,
    constellationSessions: 0,
    participationSessions: 0,
  });
  const [isUpdatingCredits, setIsUpdatingCredits] = useState(false);
  const [creditUpdateSuccess, setCreditUpdateSuccess] = useState<string | null>(null);
  const [billingModal, setBillingModal] = useState<{
    open: boolean;
    therapistId: string;
    therapistName: string;
    loading: boolean;
    total: number | null;
    totalPaid: number | null;
    error: string;
  }>({ open: false, therapistId: "", therapistName: "", loading: false, total: null, totalPaid: null, error: "" });
  const [splitPayments, setSplitPayments] = useState<any[]>([]);
  const [splitPaymentsLoading, setSplitPaymentsLoading] = useState(false);
  const [reprocessingPaymentId, setReprocessingPaymentId] = useState<string | null>(null);
  const [splitPaymentFilter, setSplitPaymentFilter] = useState<{
    status: string;
    therapistId: string;
  }>({ status: "all", therapistId: "all" });
  const [therapistSearchTerm, setTherapistSearchTerm] = useState("");
  const [isTherapistDropdownOpen, setIsTherapistDropdownOpen] = useState(false);

  // Estados para gerenciamento de cupons
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discountType: 'percentage' as 'percentage' | 'fixed',
    discountValue: '',
    expirationDate: '',
    usageLimit: '',
    status: 'active' as 'active' | 'inactive'
  });
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [couponError, setCouponError] = useState<string>('');
  const [couponSuccess, setCouponSuccess] = useState<string>('');

  // Estados para migração de créditos


  useEffect(() => {
    if (activeTab !== "therapists") return;

    const fetchData = async () => {
      try {
        setIsLoading(true);

        if (userManagementTab === "therapists") {
          const therapistsQuery = query(collection(db, "users"), where("role", "==", "terapeuta"));

          const snapshot = await getDocs(therapistsQuery);
          const therapistsList: DashboardTherapist[] = [];

          for (const userDoc of snapshot.docs) {
            const data = userDoc.data();

            // Buscar dados adicionais do therapists collection
            const therapistDoc = await getDoc(doc(db, "therapists", userDoc.id));
            const therapistData = therapistDoc.data();

            therapistsList.push({
              uid: userDoc.id,
              name: therapistData?.nomePublico || data.name || "Nome não definido",
              email: data.email || "Email não definido",
            status: data.status || TherapistStatus.ACTIVE,
            showInShowcase: therapistData?.showInShowcase ?? false,
            showcaseAutoManaged: therapistData?.showcaseAutoManaged ?? false,
            lastPaymentStatus: therapistData?.lastPaymentStatus,
            showcaseUpdatedBy: therapistData?.showcaseUpdatedBy,
            creditBalances: data.creditBalances || {
              sessions: {
                constellation: 0,
                participation: 0,
              },
              hours: 0,
            },
          });
        }

        // Ordenar alfabeticamente por nome
        therapistsList.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

        setTherapists(therapistsList);
        } else {
          // Buscar usuários regulares (não terapeutas e não administradores)
          const usersQuery = query(
            collection(db, "users"),
            where("role", "in", ["paciente", "observador"])
          );

          const snapshot = await getDocs(usersQuery);
          const usersList: any[] = [];

          snapshot.docs.forEach((userDoc) => {
            const data = userDoc.data();
            usersList.push({
              uid: userDoc.id,
              name: data.name || "Nome não definido",
              email: data.email || "Email não definido",
              role: data.role || "paciente",
              status: data.status || TherapistStatus.ACTIVE,
              creditBalances: data.creditBalances || {
                sessions: {
                  constellation: 0,
                  participation: 0,
                },
                hours: 0,
              },
            });
          });

          // Ordenar alfabeticamente por nome
          usersList.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

          setRegularUsers(usersList);
        }
      } catch (err) {
        console.error("Erro ao buscar dados:", err);
        setError("Erro ao carregar lista de usuários");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [activeTab, userManagementTab]);

  // Buscar cupons
  useEffect(() => {
    if (activeTab !== "coupons") return;

    const fetchCoupons = async () => {
      setCouponsLoading(true);
      try {
        const couponsRef = collection(db, 'coupons');
        const q = query(couponsRef);
        const snapshot = await getDocs(q);
        const couponsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Coupon[];
        
        // Ordenar por data de criação (mais recente primeiro)
        couponsList.sort((a, b) => b.createdAt?.toMillis?.() - a.createdAt?.toMillis?.() || 0);
        setCoupons(couponsList);
      } catch (error) {
        console.error('Erro ao buscar cupons:', error);
        setCouponError('Erro ao carregar cupons');
      } finally {
        setCouponsLoading(false);
      }
    };

    fetchCoupons();
  }, [activeTab]);

  // Buscar split payments
  useEffect(() => {
    if (activeTab !== "splitPayments") return;

    const fetchSplitPayments = async () => {
      try {
        setSplitPaymentsLoading(true);
        const listSplitPaymentsFunc = httpsCallable(functions, "listTherapistSplitPayments");
        
        const params: any = { limit: 100 };
        if (splitPaymentFilter.status !== "all") {
          params.status = splitPaymentFilter.status;
        }
        if (splitPaymentFilter.therapistId !== "all") {
          params.therapistId = splitPaymentFilter.therapistId;
        }

        const result = await listSplitPaymentsFunc(params);
        const data = result.data as any;
        
        if (data.success) {
          setSplitPayments(data.splitPayments || []);
        } else {
          setError("Erro ao buscar repasses");
        }
      } catch (err: any) {
        console.error("Erro ao buscar repasses:", err);
        setError(err.message || "Erro ao carregar repasses");
      } finally {
        setSplitPaymentsLoading(false);
      }
    };

    fetchSplitPayments();
  }, [activeTab, splitPaymentFilter]);

  // Reprocessar um repasse que falhou
  const reprocessSplitPayment = async (splitPaymentId: string) => {
    try {
      setReprocessingPaymentId(splitPaymentId);
      const reprocessFunc = httpsCallable(functions, "reprocessSplitPayment");
      
      const result = await reprocessFunc({ splitPaymentId });
      const data = result.data as any;
      
      if (data.success) {
        // Atualizar a lista de repasses
        setSplitPayments(prev => 
          prev.map(sp => 
            sp.id === splitPaymentId 
              ? { ...sp, status: data.status, asaasTransferId: data.asaasTransferId, errorMessage: null }
              : sp
          )
        );
        // Mostrar sucesso temporariamente
        setError(null);
      } else {
        setError(data.message || "Erro ao reprocessar repasse");
        // Atualizar com a nova mensagem de erro se houver
        if (data.errorMessage) {
          setSplitPayments(prev => 
            prev.map(sp => 
              sp.id === splitPaymentId 
                ? { ...sp, errorMessage: data.errorMessage }
                : sp
            )
          );
        }
      }
    } catch (err: any) {
      console.error("Erro ao reprocessar repasse:", err);
      setError(err.message || "Erro ao reprocessar repasse");
    } finally {
      setReprocessingPaymentId(null);
    }
  };

  const updateTherapistStatus = async (uid: string, newStatus: TherapistStatus) => {
    try {
      const userRef = doc(db, "users", uid);
      const therapistRef = doc(db, "therapists", uid);
      
      // Atualizar status em ambas as collections
      await updateDoc(userRef, { status: newStatus });
      await setDoc(therapistRef, { status: newStatus }, { merge: true });

      // Se suspender, remover da vitrine automaticamente
      if (newStatus === TherapistStatus.SUSPENDED) {
        await setDoc(therapistRef, { showInShowcase: false }, { merge: true });
        setTherapists((prev) => prev.map((therapist) => (therapist.uid === uid ? { ...therapist, status: newStatus, showInShowcase: false } : therapist)));
      } else {
        setTherapists((prev) => prev.map((therapist) => (therapist.uid === uid ? { ...therapist, status: newStatus } : therapist)));
      }
    } catch (err) {
      console.error("Erro ao atualizar status do terapeuta:", err);
      setError("Erro ao atualizar status do terapeuta");
    }
  };

  const updateUserStatus = async (uid: string, currentStatus: TherapistStatus) => {
    try {
      const userRef = doc(db, "users", uid);
      const newStatus = currentStatus === TherapistStatus.ACTIVE ? TherapistStatus.SUSPENDED : TherapistStatus.ACTIVE;

      await updateDoc(userRef, { status: newStatus });

      setRegularUsers((prev) => prev.map((user) => (user.uid === uid ? { ...user, status: newStatus } : user)));
    } catch (err) {
      console.error("Erro ao atualizar status do usuário:", err);
      setError("Erro ao atualizar status do usuário");
    }
  };

  const toggleShowInShowcase = async (uid: string, currentValue: boolean) => {
    try {
      const therapist = therapists.find(t => t.uid === uid);

      // Não permitir ativar vitrine se estiver suspenso
      if (therapist?.status === TherapistStatus.SUSPENDED && !currentValue) {
        setError("Não é possível exibir terapeuta suspenso na vitrine. Ative o terapeuta primeiro.");
        setTimeout(() => setError(""), 3000);
        return;
      }

      const therapistRef = doc(db, "therapists", uid);
      const newValue = !currentValue;

      await setDoc(therapistRef, { showInShowcase: newValue }, { merge: true });

      setTherapists((prev) => prev.map((therapist) => (therapist.uid === uid ? { ...therapist, showInShowcase: newValue } : therapist)));
    } catch (err) {
      console.error("Erro ao atualizar visibilidade na vitrine:", err);
      setError("Erro ao atualizar visibilidade na vitrine");
    }
  };

  const convertUserToTherapist = async (uid: string, userName: string, userEmail: string) => {
    try {
      const confirmResult = await Swal.fire({
        title: 'Converter para Terapeuta?',
        html: `Deseja converter <strong>${userName}</strong> em terapeuta?<br><br>Isso irá:
          <ul style="text-align: left; margin-top: 10px;">
            <li>Alterar o tipo de conta para "terapeuta"</li>
            <li>Criar perfil de terapeuta</li>
            <li>Adicionar créditos de horas para sessões</li>
          </ul>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, converter',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#4A7C75',
        cancelButtonColor: '#6B7280',
      });

      if (!confirmResult.isConfirmed) return;

      // Atualizar role na collection users
      const userRef = doc(db, "users", uid);
      await updateDoc(userRef, { role: "terapeuta" });

      // Criar documento na collection therapists
      const therapistRef = doc(db, "therapists", uid);
      await setDoc(therapistRef, {
        userId: uid,
        nomePublico: userName,
        email: userEmail,
        horasDisponiveis: 0,
        horariosDisponiveis: {},
        status: TherapistStatus.ACTIVE,
        showInShowcase: false,
        createdAt: serverTimestamp()
      });

      // Remover da lista de usuários regulares
      setRegularUsers(prev => prev.filter(u => u.uid !== uid));
      
      // Recarregar lista de terapeutas
      const therapistsQuery = query(collection(db, "users"), where("role", "==", "terapeuta"));
      const snapshot = await getDocs(therapistsQuery);
      const therapistsList: DashboardTherapist[] = [];

      for (const userDoc of snapshot.docs) {
        const data = userDoc.data();
        const therapistDoc = await getDoc(doc(db, "therapists", userDoc.id));
        const therapistData = therapistDoc.data();

        therapistsList.push({
          uid: userDoc.id,
          name: therapistData?.nomePublico || data.name || "Nome não definido",
          email: data.email || "Email não definido",
          status: data.status || TherapistStatus.ACTIVE,
          showInShowcase: therapistData?.showInShowcase ?? false,
          showcaseAutoManaged: therapistData?.showcaseAutoManaged ?? false,
          lastPaymentStatus: therapistData?.lastPaymentStatus,
          showcaseUpdatedBy: therapistData?.showcaseUpdatedBy,
          creditBalances: data.creditBalances || {
            sessions: { constellation: 0, participation: 0 },
            hours: 0,
          },
        });
      }

      therapistsList.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      setTherapists(therapistsList);

      await Swal.fire({
        title: 'Sucesso!',
        text: `${userName} foi convertido para terapeuta.`,
        icon: 'success',
        confirmButtonColor: '#4A7C75',
      });

    } catch (err) {
      console.error("Erro ao converter usuário para terapeuta:", err);
      await Swal.fire({
        title: 'Erro',
        text: 'Não foi possível converter o usuário. Tente novamente.',
        icon: 'error',
      });
    }
  };

  const convertTherapistToUser = async (uid: string, therapistName: string) => {
    try {
      const confirmResult = await Swal.fire({
        title: 'Converter para Usuário Regular?',
        html: `Deseja converter <strong>${therapistName}</strong> em usuário regular (paciente)?<br><br>Isso irá:
          <ul style="text-align: left; margin-top: 10px;">
            <li>Alterar o tipo de conta para "paciente"</li>
            <li>Remover perfil de terapeuta</li>
            <li>Manter créditos de constelações e participações</li>
            <li><strong>Remover créditos de horas</strong></li>
          </ul>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, converter',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#DC2626',
        cancelButtonColor: '#6B7280',
      });

      if (!confirmResult.isConfirmed) return;

      // Atualizar role na collection users e remover horas
      const userRef = doc(db, "users", uid);
      await updateDoc(userRef, { 
        role: "paciente",
        "creditBalances.hours": 0 
      });

      // Deletar documento da collection therapists
      const therapistRef = doc(db, "therapists", uid);
      await deleteDoc(therapistRef);

      // Remover da lista de terapeutas
      setTherapists(prev => prev.filter(t => t.uid !== uid));
      
      // Recarregar lista de usuários regulares
      const usersQuery = query(
        collection(db, "users"),
        where("role", "in", ["paciente", "observador"])
      );
      const snapshot = await getDocs(usersQuery);
      const usersList: any[] = [];

      snapshot.docs.forEach((userDoc) => {
        const data = userDoc.data();
        usersList.push({
          uid: userDoc.id,
          name: data.name || "Nome não definido",
          email: data.email || "Email não definido",
          role: data.role,
          status: data.status || TherapistStatus.ACTIVE,
          creditBalances: data.creditBalances || {
            sessions: { constellation: 0, participation: 0 },
            hours: 0,
          },
        });
      });

      usersList.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      setRegularUsers(usersList);

      await Swal.fire({
        title: 'Sucesso!',
        text: `${therapistName} foi convertido para usuário regular.`,
        icon: 'success',
        confirmButtonColor: '#4A7C75',
      });

    } catch (err) {
      console.error("Erro ao converter terapeuta para usuário:", err);
      await Swal.fire({
        title: 'Erro',
        text: 'Não foi possível converter o terapeuta. Tente novamente.',
        icon: 'error',
      });
    }
  };

  const openCreditModal = (therapist: DashboardTherapist) => {
    setCreditModal({
      therapistId: therapist.uid,
      therapistName: therapist.name,
      isTherapist: true,
      currentCredits: {
        hours: therapist.creditBalances?.hours || 0,
        constellationSessions: therapist.creditBalances?.sessions?.constellation || 0,
        participationSessions: therapist.creditBalances?.sessions?.participation || 0,
      },
    });
    setCreditFormData({
      hours: 0,
      constellationSessions: 0,
      participationSessions: 0,
    });
  };

  const openCreditModalForUser = (user: any) => {
    setCreditModal({
      therapistId: user.uid,
      therapistName: user.name,
      isTherapist: false,
      currentCredits: {
        hours: 0,
        constellationSessions: user.creditBalances?.sessions?.constellation || 0,
        participationSessions: user.creditBalances?.sessions?.participation || 0,
      },
    });
    setCreditFormData({
      hours: 0,
      constellationSessions: 0,
      participationSessions: 0,
    });
  };

  const closeCreditModal = () => {
    setCreditModal({ therapistId: null, therapistName: "", isTherapist: true, currentCredits: { hours: 0, constellationSessions: 0, participationSessions: 0 } });
    setCreditFormData({ hours: 0, constellationSessions: 0, participationSessions: 0 });
  };

  const openBillingModal = async (therapistId: string, therapistName: string) => {
    setBillingModal({ open: true, therapistId, therapistName, loading: true, total: null, totalPaid: null, error: "" });
    try {
      const q = query(
        collection(db, SPLIT_PAYMENTS_COLLECTION),
        where("therapistId", "==", therapistId)
      );
      const snapshot = await getDocs(q);
      let total = 0;
      let totalPaid = 0;
      snapshot.docs.forEach((d) => {
        const raw = d.data();
        const val = raw.commissionValue ?? 0;
        total += val;
        if (raw.status === SplitPaymentStatus.COMPLETED) totalPaid += val;
      });
      setBillingModal((prev) => ({ ...prev, loading: false, total, totalPaid }));
    } catch (err: any) {
      console.error("Erro ao carregar faturamento:", err);
      setBillingModal((prev) => ({ ...prev, loading: false, error: "Erro ao carregar faturamento." }));
    }
  };

  const updateTherapistCredits = async () => {
    if (!creditModal.therapistId) return;

    // Validar se os novos valores não resultarão em saldo negativo
    const newHours = creditModal.currentCredits.hours + creditFormData.hours;
    const newConstellation = creditModal.currentCredits.constellationSessions + creditFormData.constellationSessions;
    const newParticipation = creditModal.currentCredits.participationSessions + creditFormData.participationSessions;

    if (newHours < 0 || newConstellation < 0 || newParticipation < 0) {
      setError("Os créditos não podem ficar negativos. Verifique os valores inseridos.");
      closeCreditModal();
      setTimeout(() => setError(""), 5000);
      return;
    }

    try {
      setIsUpdatingCredits(true);
      const userRef = doc(db, "users", creditModal.therapistId);

      // Atualizar créditos usando increment
      const updates: any = {};
      
      if (creditFormData.hours !== 0) {
        updates["creditBalances.hours"] = increment(creditFormData.hours);
      }
      if (creditFormData.constellationSessions !== 0) {
        updates["creditBalances.sessions.constellation"] = increment(creditFormData.constellationSessions);
      }
      if (creditFormData.participationSessions !== 0) {
        updates["creditBalances.sessions.participation"] = increment(creditFormData.participationSessions);
      }

      if (Object.keys(updates).length > 0) {
        updates["creditBalances.updatedAt"] = serverTimestamp();
        await updateDoc(userRef, updates);

        const newCredits = {
          hours: newHours,
          sessions: {
            constellation: newConstellation,
            participation: newParticipation,
          },
        };

        // Atualizar estado local apropriado
        if (creditModal.isTherapist) {
          setTherapists((prev) =>
            prev.map((therapist) => {
              if (therapist.uid === creditModal.therapistId) {
                return { ...therapist, creditBalances: newCredits };
              }
              return therapist;
            })
          );
        } else {
          setRegularUsers((prev) =>
            prev.map((user) => {
              if (user.uid === creditModal.therapistId) {
                return { ...user, creditBalances: newCredits };
              }
              return user;
            })
          );
        }

        // Mostrar mensagem de sucesso
        setCreditUpdateSuccess(`Créditos atualizados com sucesso para ${creditModal.therapistName}`);
        setTimeout(() => setCreditUpdateSuccess(null), 5000);
      }

      closeCreditModal();
    } catch (err) {
      console.error("Erro ao atualizar créditos:", err);
      setError("Erro ao atualizar créditos do terapeuta");
    } finally {
      setIsUpdatingCredits(false);
    }
  };

  const getTimeBalanceLabel = (availableHours: number) => {
    const totalMinutes = Math.round(availableHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  // Funções para gerenciamento de cupons
  const handleCouponFormChange = (field: string, value: any) => {
    setCouponForm(prev => ({ ...prev, [field]: value }));
    setCouponError('');
  };

  const validateCouponForm = () => {
    if (!couponForm.code.trim()) {
      setCouponError('Código do cupom é obrigatório');
      return false;
    }
    if (!/^[A-Z0-9]+$/.test(couponForm.code)) {
      setCouponError('Código deve conter apenas letras maiúsculas e números');
      return false;
    }
    if (!couponForm.discountValue || parseFloat(couponForm.discountValue) <= 0) {
      setCouponError('Valor do desconto deve ser maior que zero');
      return false;
    }
    if (couponForm.discountType === 'percentage' && parseFloat(couponForm.discountValue) > 100) {
      setCouponError('Desconto em porcentagem não pode ser maior que 100%');
      return false;
    }
    if (!couponForm.expirationDate) {
      setCouponError('Data de validade é obrigatória');
      return false;
    }
    if (new Date(couponForm.expirationDate) < new Date()) {
      setCouponError('Data de validade não pode ser no passado');
      return false;
    }
    if (!couponForm.usageLimit || parseInt(couponForm.usageLimit) <= 0) {
      setCouponError('Limite de usos deve ser maior que zero');
      return false;
    }
    return true;
  };

  const handleCreateCoupon = async () => {
    if (!validateCouponForm()) return;

    setSavingCoupon(true);
    setCouponError('');

    try {
      // Verificar se o código já existe
      const couponsRef = collection(db, 'coupons');
      const q = query(couponsRef, where('code', '==', couponForm.code.toUpperCase()));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty && !editingCoupon) {
        setCouponError('Já existe um cupom com este código');
        setSavingCoupon(false);
        return;
      }

      const couponData = {
        code: couponForm.code.toUpperCase(),
        discountType: couponForm.discountType,
        discountValue: parseFloat(couponForm.discountValue),
        expirationDate: couponForm.expirationDate,
        usageLimit: parseInt(couponForm.usageLimit),
        status: couponForm.status,
        updatedAt: serverTimestamp()
      };

      if (editingCoupon) {
        // Atualizar cupom existente
        const couponRef = doc(db, 'coupons', editingCoupon.id);
        await updateDoc(couponRef, couponData);
        setCouponSuccess('Cupom atualizado com sucesso!');
      } else {
        // Criar novo cupom
        const newCouponRef = doc(collection(db, 'coupons'));
        await setDoc(newCouponRef, {
          ...couponData,
          usedCount: 0,
          createdAt: serverTimestamp()
        });
        setCouponSuccess('Cupom criado com sucesso!');
      }

      // Resetar formulário
      setCouponForm({
        code: '',
        discountType: 'percentage',
        discountValue: '',
        expirationDate: '',
        usageLimit: '',
        status: 'active'
      });
      setEditingCoupon(null);

      // Recarregar lista de cupons
      const updatedSnapshot = await getDocs(couponsRef);
      const couponsList = updatedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Coupon[];
      couponsList.sort((a, b) => b.createdAt?.toMillis?.() - a.createdAt?.toMillis?.() || 0);
      setCoupons(couponsList);

      setTimeout(() => setCouponSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao salvar cupom:', error);
      setCouponError('Erro ao salvar cupom. Tente novamente.');
    } finally {
      setSavingCoupon(false);
    }
  };

  const handleEditCoupon = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue.toString(),
      expirationDate: coupon.expirationDate,
      usageLimit: coupon.usageLimit.toString(),
      status: coupon.status
    });
    setCouponError('');
    setCouponSuccess('');
    // Scroll para o formulário
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingCoupon(null);
    setCouponForm({
      code: '',
      discountType: 'percentage',
      discountValue: '',
      expirationDate: '',
      usageLimit: '',
      status: 'active'
    });
    setCouponError('');
    setCouponSuccess('');
  };

  const handleDeleteCoupon = async (couponId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este cupom?')) return;

    try {
      const couponRef = doc(db, 'coupons', couponId);
      await updateDoc(couponRef, { status: 'inactive' });
      
      // Atualizar lista local
      setCoupons(prev => prev.map(c => 
        c.id === couponId ? { ...c, status: 'inactive' as const } : c
      ));
      
      setCouponSuccess('Cupom desativado com sucesso!');
      setTimeout(() => setCouponSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao desativar cupom:', error);
      setCouponError('Erro ao desativar cupom');
    }
  };

  const tabs = [
    { id: "therapists", label: "Gestão de Usuários", icon: Users },
    { id: "appointments", label: "Visão Geral de Consultas", icon: Calendar },
    { id: "content", label: "Editar Conteúdo da Home", icon: FileText },
    { id: "plans", label: "Gestão de Planos", icon: DollarSign },
    { id: "splitPayments", label: "Repasses", icon: DollarSign },
    { id: "coupons", label: "Cupons", icon: Ticket },
  ] as const;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Painel de Administração</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {creditUpdateSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800">{creditUpdateSuccess}</span>
          </div>
        </div>
      )}

      {/* Navegação por Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="flex gap-3 sm:gap-6 min-w-max">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}>
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Conteúdo das Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        {activeTab === "therapists" && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Gestão de Usuários
            </h2>

            {/* Sub-tabs para Terapeutas e Usuários */}
            <div className="mb-6 border-b border-gray-200">
              <nav className="flex gap-4">
                <button
                  onClick={() => {
                    setUserManagementTab("therapists");
                    setSearchFilter("");
                  }}
                  className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${
                    userManagementTab === "therapists"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Terapeutas
                </button>
                <button
                  onClick={() => {
                    setUserManagementTab("users");
                    setSearchFilter("");
                  }}
                  className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${
                    userManagementTab === "users"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Usuários
                </button>
              </nav>
            </div>

            {/* Campo de Filtro */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder={`Buscar ${userManagementTab === "therapists" ? "terapeuta" : "usuário"} por nome ou email...`}
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="ml-2">Carregando dados...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const dataToShow = userManagementTab === "therapists" ? therapists : regularUsers;
                  const filteredData = dataToShow.filter((item) => {
                    const searchLower = searchFilter.toLowerCase();
                    return (
                      item.name.toLowerCase().includes(searchLower) ||
                      item.email.toLowerCase().includes(searchLower)
                    );
                  });

                  if (filteredData.length === 0) {
                    return (
                      <p className="text-gray-500 text-center py-4">
                        {searchFilter ? `Nenhum ${userManagementTab === "therapists" ? "terapeuta" : "usuário"} encontrado com esse termo de busca` : `Nenhum ${userManagementTab === "therapists" ? "terapeuta" : "usuário"} encontrado`}
                      </p>
                    );
                  }

                  return userManagementTab === "therapists" ? (
                    // Renderizar Terapeutas
                    filteredData.map((therapist: DashboardTherapist) => (
                    <div key={therapist.uid} className="border border-gray-200 rounded-lg p-4">
                      {/* Informações do Terapeuta */}
                      <div className="mb-3">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900">{therapist.name}</h3>
                            <p className="text-sm text-gray-600 break-all">{therapist.email}</p>
                          </div>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${therapist.status === TherapistStatus.ACTIVE ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                              }`}>
                            {therapist.status === TherapistStatus.ACTIVE ? "Ativo" : "Suspenso"}
                          </span>
                        </div>
                        
                        {/* Exibição de Créditos */}
                        <div className="flex flex-wrap gap-2">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                            <Clock className="w-3 h-3" />
                            {getTimeBalanceLabel(therapist.creditBalances?.hours || 0)}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs">
                            <Star className="w-3 h-3" />
                            {therapist.creditBalances?.sessions?.constellation || 0} constelações
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs">
                            <Users className="w-3 h-3" />
                            {therapist.creditBalances?.sessions?.participation || 0} participações
                          </span>
                        </div>
                      </div>

                      {/* Ações - Movidas para o fundo */}
                      <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => openCreditModal(therapist)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors text-sm"
                            title="Gerenciar créditos">
                            <DollarSign className="w-4 h-4" />
                            <span className="hidden sm:inline">Créditos</span>
                          </button>

                          <button
                            onClick={() => openBillingModal(therapist.uid, therapist.name)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors text-sm"
                            title="Ver faturamento">
                            <TrendingUp className="w-4 h-4" />
                            <span className="hidden sm:inline">Faturamento</span>
                          </button>

                          <button
                            onClick={() => convertTherapistToUser(therapist.uid, therapist.name)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-orange-600 hover:bg-orange-50 transition-colors text-sm font-medium"
                            title="Converter para usuário regular"
                          >
                            <User className="w-4 h-4" />
                            <span className="hidden sm:inline">Tornar Usuário</span>
                          </button>

                          <label className="flex items-center gap-2 cursor-pointer" title={therapist.status === TherapistStatus.SUSPENDED ? "Terapeuta suspenso não pode aparecer na vitrine" : "Mostrar na vitrine"}>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-600">Vitrine</span>
                              {therapist.showcaseAutoManaged && (
                                <span 
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700"
                                  title={`Gerenciamento automático por pagamento${therapist.lastPaymentStatus ? ` (${therapist.lastPaymentStatus})` : ''}`}
                                >
                                  Auto
                                </span>
                              )}
                            </div>
                            <input
                              type="checkbox"
                              checked={therapist.showInShowcase}
                              onChange={() => toggleShowInShowcase(therapist.uid, therapist.showInShowcase ?? false)}
                              disabled={therapist.status === TherapistStatus.SUSPENDED}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </label>
                        </div>

                        <button
                          onClick={() => updateTherapistStatus(therapist.uid, therapist.status === TherapistStatus.ACTIVE ? TherapistStatus.SUSPENDED : TherapistStatus.ACTIVE)}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium ${therapist.status === TherapistStatus.ACTIVE ? "text-red-600 hover:bg-red-50" : "text-green-600 hover:bg-green-50"
                            }`}
                          title={therapist.status === TherapistStatus.ACTIVE ? "Suspender" : "Ativar"}>
                          {therapist.status === TherapistStatus.ACTIVE ? (
                            <>
                              <UserX className="w-4 h-4" />
                              <span className="hidden sm:inline">Suspender</span>
                            </>
                          ) : (
                            <>
                              <UserCheck className="w-4 h-4" />
                              <span className="hidden sm:inline">Ativar</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))
                  ) : (
                    // Renderizar Usuários Regulares
                    filteredData.map((user: any) => (
                      <div key={user.uid} className="border border-gray-200 rounded-lg p-4">
                        {/* Informações do Usuário */}
                        <div className="mb-3">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900">{user.name}</h3>
                              <p className="text-sm text-gray-600 break-all">{user.email}</p>
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                                user.status === TherapistStatus.ACTIVE ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                              }`}
                            >
                              {user.status === TherapistStatus.ACTIVE ? "Ativo" : "Suspenso"}
                            </span>
                          </div>
                          
                          {/* Papel e Créditos */}
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                              {user.role === "paciente" ? "Paciente" : "Observador"}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs">
                              <Star className="w-3 h-3" />
                              {user.creditBalances?.sessions?.constellation || 0} constelações
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs">
                              <Users className="w-3 h-3" />
                              {user.creditBalances?.sessions?.participation || 0} participações
                            </span>
                          </div>
                        </div>

                        {/* Ações */}
                        <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => openCreditModalForUser(user)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors text-sm"
                              title="Gerenciar créditos"
                            >
                              <DollarSign className="w-4 h-4" />
                              <span className="hidden sm:inline">Créditos</span>
                            </button>

                            <button
                              onClick={() => convertUserToTherapist(user.uid, user.name, user.email)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors text-sm font-medium"
                              title="Converter para terapeuta"
                            >
                              <UserCheck className="w-4 h-4" />
                              <span className="hidden sm:inline">Tornar Terapeuta</span>
                            </button>
                          </div>

                          <button
                            onClick={() => updateUserStatus(user.uid, user.status || TherapistStatus.ACTIVE)}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium ${
                              user.status === TherapistStatus.ACTIVE ? "text-red-600 hover:bg-red-50" : "text-green-600 hover:bg-green-50"
                            }`}
                            title={user.status === TherapistStatus.ACTIVE ? "Suspender" : "Ativar"}
                          >
                            {user.status === TherapistStatus.ACTIVE ? (
                              <>
                                <UserX className="w-4 h-4" />
                                <span className="hidden sm:inline">Suspender</span>
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-4 h-4" />
                                <span className="hidden sm:inline">Ativar</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ))
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {activeTab === "appointments" && (
          <div className="p-4 sm:p-6">
            <AppointmentsOverview />
          </div>
        )}

        {activeTab === "content" && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Editar Conteúdo da Home Page
            </h2>
            <HomeContentEditor />
          </div>
        )}

        {activeTab === "plans" && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Gestão de Planos de Pagamento
            </h2>
            <PlansManager />
          </div>
        )}

        {activeTab === "splitPayments" && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Gestão de Repasses
            </h2>
            
            {/* Filtros */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={splitPaymentFilter.status}
                  onChange={(e) => setSplitPaymentFilter(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Todos os status</option>
                  <option value="PENDING">Pendente</option>
                  <option value="PROCESSING">Processando</option>
                  <option value="COMPLETED">Concluído</option>
                  <option value="FAILED">Falhou</option>
                </select>
              </div>
              
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Terapeuta</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Buscar terapeuta..."
                    value={therapistSearchTerm}
                    onChange={(e) => setTherapistSearchTerm(e.target.value)}
                    onFocus={() => setIsTherapistDropdownOpen(true)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setIsTherapistDropdownOpen(!isTherapistDropdownOpen)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {splitPaymentFilter.therapistId !== "all" && (
                    <button
                      type="button"
                      onClick={() => {
                        setSplitPaymentFilter(prev => ({ ...prev, therapistId: "all" }));
                        setTherapistSearchTerm("");
                      }}
                      className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Limpar seleção"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {isTherapistDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setIsTherapistDropdownOpen(false)}
                    />
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <button
                        onClick={() => {
                          setSplitPaymentFilter(prev => ({ ...prev, therapistId: "all" }));
                          setTherapistSearchTerm("");
                          setIsTherapistDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors ${
                          splitPaymentFilter.therapistId === "all" ? "bg-blue-100 font-medium" : ""
                        }`}
                      >
                        Todos os terapeutas
                      </button>
                      {therapists
                        .filter(therapist => 
                          therapist.name.toLowerCase().includes(therapistSearchTerm.toLowerCase()) ||
                          therapist.email.toLowerCase().includes(therapistSearchTerm.toLowerCase())
                        )
                        .map(therapist => (
                          <button
                            key={therapist.uid}
                            onClick={() => {
                              setSplitPaymentFilter(prev => ({ ...prev, therapistId: therapist.uid }));
                              setTherapistSearchTerm(therapist.name);
                              setIsTherapistDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-t border-gray-100 ${
                              splitPaymentFilter.therapistId === therapist.uid ? "bg-blue-100 font-medium" : ""
                            }`}
                          >
                            <div className="font-medium text-gray-900">{therapist.name}</div>
                            <div className="text-xs text-gray-500">{therapist.email}</div>
                          </button>
                        ))
                      }
                      {therapists.filter(therapist => 
                        therapist.name.toLowerCase().includes(therapistSearchTerm.toLowerCase()) ||
                        therapist.email.toLowerCase().includes(therapistSearchTerm.toLowerCase())
                      ).length === 0 && therapistSearchTerm && (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          Nenhum terapeuta encontrado
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Lista de Repasses */}
            {splitPaymentsLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="ml-2">Carregando repasses...</span>
              </div>
            ) : splitPayments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum repasse encontrado</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Resumo */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total de repasses:</span>
                      <p className="font-semibold text-gray-900">{splitPayments.length}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Valor total:</span>
                      <p className="font-semibold text-gray-900">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                          splitPayments.reduce((sum, sp) => sum + (sp.commissionValue || 0), 0)
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Pendentes:</span>
                      <p className="font-semibold text-yellow-700">
                        {splitPayments.filter(sp => sp.status === 'PENDING').length}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Concluídos:</span>
                      <p className="font-semibold text-green-700">
                        {splitPayments.filter(sp => sp.status === 'COMPLETED').length}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Com Erro:</span>
                      <p className="font-semibold text-red-700">
                        {splitPayments.filter(sp => sp.status === 'FAILED').length}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tabela de Repasses */}
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                      <div className="max-h-[600px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Data da Sessão</th>
                              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Terapeuta</th>
                              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Paciente</th>
                              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Valor Cobrança</th>
                              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Comissão</th>
                              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Valor Repasse</th>
                              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Detalhes</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {splitPayments.map((splitPayment) => (
                              <tr key={splitPayment.id} className="hover:bg-gray-50">
                                <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {splitPayment.appointmentDate ? new Date(splitPayment.appointmentDate).toLocaleDateString('pt-BR') : '-'}
                                </td>
                                <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-sm">
                                  <div className="min-w-[120px]">
                                    <div className="font-medium text-gray-900">{splitPayment.therapistName || 'N/A'}</div>
                                    <div className="text-gray-500 text-xs truncate max-w-[150px]">{splitPayment.therapistEmail || ''}</div>
                                  </div>
                                </td>
                                <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-sm">
                                  <div className="min-w-[120px]">
                                    <div className="font-medium text-gray-900">{splitPayment.patientName || 'N/A'}</div>
                                    <div className="text-gray-500 text-xs truncate max-w-[150px]">{splitPayment.patientEmail || ''}</div>
                                  </div>
                                </td>
                                <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(splitPayment.chargeValue || 0)}
                                </td>
                                <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {splitPayment.therapistCommission || 0}%
                                </td>
                                <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-sm font-semibold text-green-700">
                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(splitPayment.commissionValue || 0)}
                                </td>
                                <td className="px-2 sm:px-4 py-3 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    splitPayment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                    splitPayment.status === 'PROCESSING' ? 'bg-blue-100 text-blue-800' :
                                    splitPayment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                    splitPayment.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {splitPayment.status === 'PENDING' ? 'Pendente' :
                                     splitPayment.status === 'PROCESSING' ? 'Processando' :
                                     splitPayment.status === 'COMPLETED' ? 'Concluído' :
                                     splitPayment.status === 'FAILED' ? 'Falhou' :
                                     splitPayment.status}
                                  </span>
                                </td>
                                <td className="px-2 sm:px-4 py-3 text-sm">
                                  {splitPayment.status === 'FAILED' && splitPayment.errorMessage ? (
                                    <div className="space-y-2">
                                      <div className="flex items-start gap-2 max-w-xs">
                                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                        <span className="text-red-700 text-xs break-words">{splitPayment.errorMessage}</span>
                                      </div>
                                      <button
                                        onClick={() => reprocessSplitPayment(splitPayment.id)}
                                        disabled={reprocessingPaymentId === splitPayment.id}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition-colors"
                                      >
                                        {reprocessingPaymentId === splitPayment.id ? (
                                          <>
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                            <span>Processando...</span>
                                          </>
                                        ) : (
                                          <>
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            <span>Reprocessar</span>
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  ) : splitPayment.status === 'COMPLETED' && splitPayment.asaasTransferId ? (
                                    <div className="flex items-start gap-2">
                                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                      <span className="text-gray-600 text-xs font-mono">{splitPayment.asaasTransferId}</span>
                                    </div>
                                  ) : splitPayment.status === 'PROCESSING' ? (
                                    <div className="flex items-center gap-2">
                                      <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                                      <span className="text-gray-600 text-xs">Processando...</span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {activeTab === "coupons" && (
        <div className="p-3 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Ticket className="w-5 h-5" />
            Gerenciamento de Cupons
          </h2>

          {/* Mensagens de feedback */}
          {couponError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="text-sm sm:text-base text-red-800">{couponError}</span>
              </div>
            </div>
          )}

          {couponSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-sm sm:text-base text-green-800">{couponSuccess}</span>
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:gap-6">
            {/* Formulário para criar/editar cupom */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">
                {editingCoupon ? 'Editar Cupom' : 'Criar Novo Cupom'}
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Código do Cupom *</label>
                  <input
                    type="text"
                    placeholder="Ex: PROMO2026"
                    value={couponForm.code}
                    onChange={(e) => handleCouponFormChange('code', e.target.value.toUpperCase())}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                    disabled={savingCoupon}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Desconto *</label>
                  <select
                    value={couponForm.discountType}
                    onChange={(e) => handleCouponFormChange('discountType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={savingCoupon}
                  >
                    <option value="percentage">Porcentagem (%)</option>
                    <option value="fixed">Valor Fixo (R$)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valor do Desconto * {couponForm.discountType === 'percentage' ? '(%)' : '(R$)'}
                  </label>
                  <input
                    type="number"
                    placeholder={couponForm.discountType === 'percentage' ? 'Ex: 10' : 'Ex: 50.00'}
                    value={couponForm.discountValue}
                    onChange={(e) => handleCouponFormChange('discountValue', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    max={couponForm.discountType === 'percentage' ? '100' : undefined}
                    step={couponForm.discountType === 'percentage' ? '1' : '0.01'}
                    disabled={savingCoupon}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data de Validade *</label>
                  <input
                    type="date"
                    value={couponForm.expirationDate}
                    onChange={(e) => handleCouponFormChange('expirationDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min={new Date().toISOString().split('T')[0]}
                    disabled={savingCoupon}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Limite de Usos *</label>
                  <input
                    type="number"
                    placeholder="Ex: 100"
                    value={couponForm.usageLimit}
                    onChange={(e) => handleCouponFormChange('usageLimit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    disabled={savingCoupon}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                  <select
                    value={couponForm.status}
                    onChange={(e) => handleCouponFormChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={savingCoupon}
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleCreateCoupon}
                  className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={savingCoupon}
                >
                  {savingCoupon ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingCoupon ? 'Atualizar Cupom' : 'Criar Cupom'}
                    </>
                  )}
                </button>
                {editingCoupon && (
                  <button
                    onClick={handleCancelEdit}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    disabled={savingCoupon}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </div>

            {/* Lista de cupons existentes */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Cupons Cadastrados</h3>
              
              {couponsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-sm sm:text-base text-gray-600">Carregando cupons...</span>
                </div>
              ) : coupons.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Ticket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm sm:text-base">Nenhum cupom cadastrado ainda</p>
                </div>
              ) : (
                <>
                  {/* Versão Desktop - Tabela */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Desconto</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Validade</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usos</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {coupons.map((coupon) => {
                          const isExpired = new Date(coupon.expirationDate) < new Date();
                          const isMaxUsed = coupon.usedCount >= coupon.usageLimit;
                          const isActive = coupon.status === 'active' && !isExpired && !isMaxUsed;
                          
                          return (
                            <tr key={coupon.id} className={!isActive ? 'opacity-60' : ''}>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <span className="font-mono font-semibold text-gray-900">{coupon.code}</span>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className="text-gray-900">
                                  {coupon.discountType === 'percentage' 
                                    ? `${coupon.discountValue}%`
                                    : `R$ ${coupon.discountValue.toFixed(2)}`
                                  }
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className={`text-sm ${isExpired ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                                  {new Date(coupon.expirationDate).toLocaleDateString('pt-BR')}
                                  {isExpired && ' (Expirado)'}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className={`text-sm ${isMaxUsed ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                                  {coupon.usedCount} / {coupon.usageLimit}
                                  {isMaxUsed && ' (Esgotado)'}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  isActive
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {isActive ? 'Ativo' : 'Inativo'}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEditCoupon(coupon)}
                                    className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                                    title="Editar cupom"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  {coupon.status === 'active' && (
                                    <button
                                      onClick={() => handleDeleteCoupon(coupon.id)}
                                      className="text-red-600 hover:text-red-900 flex items-center gap-1"
                                      title="Desativar cupom"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Versão Mobile - Cards */}
                  <div className="md:hidden space-y-3">
                    {coupons.map((coupon) => {
                      const isExpired = new Date(coupon.expirationDate) < new Date();
                      const isMaxUsed = coupon.usedCount >= coupon.usageLimit;
                      const isActive = coupon.status === 'active' && !isExpired && !isMaxUsed;
                      
                      return (
                        <div key={coupon.id} className={`border border-gray-200 rounded-lg p-4 ${!isActive ? 'opacity-60' : ''}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <span className="font-mono font-semibold text-gray-900 text-lg">{coupon.code}</span>
                              <div className="mt-1">
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  isActive
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {isActive ? 'Ativo' : 'Inativo'}
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditCoupon(coupon)}
                                className="text-blue-600 hover:text-blue-900 p-2"
                                title="Editar cupom"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              {coupon.status === 'active' && (
                                <button
                                  onClick={() => handleDeleteCoupon(coupon.id)}
                                  className="text-red-600 hover:text-red-900 p-2"
                                  title="Desativar cupom"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-500 block mb-1">Desconto</span>
                              <span className="text-gray-900 font-medium">
                                {coupon.discountType === 'percentage' 
                                  ? `${coupon.discountValue}%`
                                  : `R$ ${coupon.discountValue.toFixed(2)}`
                                }
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 block mb-1">Usos</span>
                              <span className={`font-medium ${isMaxUsed ? 'text-red-600' : 'text-gray-900'}`}>
                                {coupon.usedCount} / {coupon.usageLimit}
                                {isMaxUsed && <span className="text-xs block">(Esgotado)</span>}
                              </span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-500 block mb-1">Validade</span>
                              <span className={`font-medium ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                                {new Date(coupon.expirationDate).toLocaleDateString('pt-BR')}
                                {isExpired && ' (Expirado)'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {creditModal.therapistId && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 z-50 transition-opacity"
            onClick={closeCreditModal}
          />
          
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                    <h3 className="text-xl font-semibold text-gray-900">Gerenciar Créditos</h3>
                  </div>
                  <button
                    onClick={closeCreditModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">{creditModal.therapistName}</p>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                    <p className="text-xs text-gray-600">Créditos atuais:</p>
                    <div className="flex gap-3 text-sm flex-wrap">
                      {creditModal.isTherapist && (
                        <span className="text-blue-700 font-medium">{getTimeBalanceLabel(creditModal.currentCredits.hours)}</span>
                      )}
                      <span className="text-purple-700 font-medium">{creditModal.currentCredits.constellationSessions} constelações</span>
                      <span className="text-teal-700 font-medium">{creditModal.currentCredits.participationSessions} participações</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {creditModal.isTherapist && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Horas (adicionar/remover)
                      </label>
                      <input
                        type="number"
                        value={creditFormData.hours}
                        onChange={(e) => setCreditFormData({ ...creditFormData, hours: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Ex: 10 (adiciona) ou -5 (remove)"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sessões de Constelação (adicionar/remover)
                    </label>
                    <input
                      type="number"
                      value={creditFormData.constellationSessions}
                      onChange={(e) => setCreditFormData({ ...creditFormData, constellationSessions: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 5 (adiciona) ou -2 (remove)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sessões de Participação (adicionar/remover)
                    </label>
                    <input
                      type="number"
                      value={creditFormData.participationSessions}
                      onChange={(e) => setCreditFormData({ ...creditFormData, participationSessions: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 3 (adiciona) ou -1 (remove)"
                    />
                  </div>
                </div>

                <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
                  <button
                    onClick={closeCreditModal}
                    disabled={isUpdatingCredits}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={updateTherapistCredits}
                    disabled={isUpdatingCredits || (creditModal.isTherapist ? (creditFormData.hours === 0 && creditFormData.constellationSessions === 0 && creditFormData.participationSessions === 0) : (creditFormData.constellationSessions === 0 && creditFormData.participationSessions === 0))}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isUpdatingCredits ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Atualizando...
                      </>
                    ) : (
                      "Atualizar Créditos"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {billingModal.open && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-50"
            onClick={() => setBillingModal((prev) => ({ ...prev, open: false }))}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Faturamento</h3>
                <button
                  onClick={() => setBillingModal((prev) => ({ ...prev, open: false }))}
                  className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-6">{billingModal.therapistName}</p>
              {billingModal.loading && (
                <div className="flex items-center gap-2 text-gray-500 py-4">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Carregando...</span>
                </div>
              )}
              {billingModal.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{billingModal.error}</div>
              )}
              {!billingModal.loading && billingModal.total !== null && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Total em repasses</span>
                    <span className="font-semibold text-gray-900">
                      {billingModal.total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                    <span className="text-sm text-emerald-700">Total pago (Concluído)</span>
                    <span className="font-bold text-emerald-700">
                      {(billingModal.totalPaid ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}


    </div>
  );
};

// Sub-componente: Painel do Terapeuta
const TherapistDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"sessions" | "hours" | "perfil" | "subscriptions" | "settings" | "historico">("sessions");
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [participantCounts, setParticipantCounts] = useState<{ [appointmentId: string]: number }>({});
  const [therapistData, setTherapistData] = useState<TherapistData>({ 
    avatar: null,
    creditBalances: { 
      sessions: { constellation: 0, participation: 0 }, 
      hours: 0 
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(true); // Estado de carregamento para o perfil
  const [workingHours, setWorkingHours] = useState<{
    [key: string]: { inicio: string; fim: string }[];
  }>({});
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [pixKey, setPixKey] = useState<string>("");
  const [pixKeyType, setPixKeyType] = useState<PixKeyType>("CPF");
  const [pixSaving, setPixSaving] = useState<boolean>(false);
  const [pixSaveStatus, setPixSaveStatus] = useState<"idle" | "success" | "error">("idle");
  const [pixErrorMessage, setPixErrorMessage] = useState<string>("");

  const [isConsumingHours, setIsConsumingHours] = useState<{ [key: string]: boolean }>({});
  const [consumeError, setConsumeError] = useState<string>("");
  const [sessionDuration, setSessionDuration] = useState<number>(60); // duração em minutos
  const [durationSaving, setDurationSaving] = useState<boolean>(false);
  const [customDuration, setCustomDuration] = useState<string>("");
  const [isCustomDuration, setIsCustomDuration] = useState<boolean>(false);
  const [durationSaveStatus, setDurationSaveStatus] = useState<"idle" | "success" | "error">("idle");
  const [durationErrorMessage, setDurationErrorMessage] = useState<string>("");
  const [workingHoursSaveStatus, setWorkingHoursSaveStatus] = useState<"idle" | "success" | "error">("idle");

  // Estados para controlar visibilidade pública dos appointments
  const [isUpdatingPublic, setIsUpdatingPublic] = useState<{ [key: string]: boolean }>({});
  const [publicUpdateError, setPublicUpdateError] = useState<string>("");

  // Estado para controlar o chat aberto
  const [openChat, setOpenChat] = useState<{
    therapistId: string;
    therapistName: string;
    otherMemberId: string;
    otherMemberName: string;
    otherMemberRole: ParticipantRole;
  } | null>(null);

  // Estado para controlar cards de participantes expandidos
  const [expandedParticipants, setExpandedParticipants] = useState<{ [appointmentId: string]: boolean }>({});
  const [appointmentParticipants, setAppointmentParticipants] = useState<{ [appointmentId: string]: any[] }>({});
  const [loadingParticipants, setLoadingParticipants] = useState<{ [appointmentId: string]: boolean }>({});

  // Estado para rastrear mensagens não lidas
  const [unreadMessages, setUnreadMessages] = useState<{ [chatId: string]: number }>({});

  // Estados para o histórico financeiro (split payments)
  const [paymentHistory, setPaymentHistory] = useState<SplitPayment[]>([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistoryError, setPaymentHistoryError] = useState<string>("");
  const [paymentHistoryDateFrom, setPaymentHistoryDateFrom] = useState<string>("");
  const [paymentHistoryDateTo, setPaymentHistoryDateTo] = useState<string>("");

  const [rescheduleModal, setRescheduleModal] = useState<{
    appointmentId: string | null;
    dateTime: string;
    isSubmitting: boolean;
    error: string;
  }>({ appointmentId: null, dateTime: "", isSubmitting: false, error: "" });
  const [rescheduleFeedback, setRescheduleFeedback] = useState<
    | {
      type: "success" | "error";
      message: string;
    }
    | null
  >(null);

  // Usa apenas o novo sistema de créditos (creditBalances.hours)
  const availableHours = therapistData.creditBalances?.hours ?? 0;
  const hasEnoughHours = availableHours > 0;

  const getTimeBalanceLabel = () => {
    const totalMinutes = Math.round(availableHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  const formatDateTimeLocal = (date: Date) => {
    const pad = (value: number) => value.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
      date.getMinutes()
    )}`;
  };

  const openRescheduleModal = (appointment: Appointment) => {
    const sessionTime = appointment.dataSessao?.toDate ? appointment.dataSessao.toDate() : new Date();
    setRescheduleModal({
      appointmentId: appointment.id,
      dateTime: formatDateTimeLocal(sessionTime),
      isSubmitting: false,
      error: "",
    });
  };

  const closeRescheduleModal = () => {
    setRescheduleModal({ appointmentId: null, dateTime: "", isSubmitting: false, error: "" });
  };

  const dismissRescheduleFeedback = () => setRescheduleFeedback(null);

  const isAppointmentConfirmed = (status: string) => {
    return status === AppointmentStatus.CONFIRMED;
  }

  const toggleParticipantsList = async (appointmentId: string) => {
    const isExpanded = expandedParticipants[appointmentId];
    
    // Se está fechando, apenas fecha
    if (isExpanded) {
      setExpandedParticipants(prev => ({
        ...prev,
        [appointmentId]: false
      }));
      return;
    }
    
    // Se está expandindo, abre imediatamente e depois carrega se necessário
    setExpandedParticipants(prev => ({
      ...prev,
      [appointmentId]: true
    }));
    
    // Se ainda não carregou os participantes, busca
    if (!appointmentParticipants[appointmentId]) {
      setLoadingParticipants(prev => ({ ...prev, [appointmentId]: true }));
      
      try {
        const getParticipants = httpsCallable(functions, 'getAppointmentParticipants');
        const result = await getParticipants({ appointmentId }) as any;
        
        if (result.data.success) {
          setAppointmentParticipants(prev => ({
            ...prev,
            [appointmentId]: result.data.participants
          }));
        }
      } catch (error: any) {
        console.error('Erro ao buscar participantes:', error);
      } finally {
        setLoadingParticipants(prev => ({ ...prev, [appointmentId]: false }));
      }
    }
  };

  const handleRescheduleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!rescheduleModal.appointmentId) return;

    const appointmentToReschedule = appointments.find((appointment) => appointment.id === rescheduleModal.appointmentId);
    if (!appointmentToReschedule || !isAppointmentConfirmed(appointmentToReschedule.status)) {
      setRescheduleModal((prev) => ({ ...prev, error: "Apenas sessões confirmadas podem ser reagendadas." }));
      return;
    }

    const parsedDate = new Date(rescheduleModal.dateTime);
    if (Number.isNaN(parsedDate.getTime())) {
      setRescheduleModal((prev) => ({ ...prev, error: "Informe uma data e hora válidas." }));
      return;
    }

    setRescheduleModal((prev) => ({ ...prev, isSubmitting: true, error: "" }));

    try {
      const rescheduleAppointmentFn = httpsCallable(functions, "rescheduleAppointment");
      await rescheduleAppointmentFn({
        appointmentId: rescheduleModal.appointmentId,
        newDateTime: rescheduleModal.dateTime,
      });

      setRescheduleFeedback({ type: "success", message: "Sessão reagendada com sucesso." });
      closeRescheduleModal();
    } catch (error: any) {
      console.error("Erro ao reagendar sessão:", error);
      let message = "Não foi possível reagendar a sessão.";
      if (error?.message) message = error.message;
      if (error?.code && typeof error.code === "string") {
        switch (error.code) {
          case "functions/permission-denied":
            message = "Você não possui permissão para alterar este agendamento.";
            break;
          case "functions/not-found":
            message = "Agendamento não encontrado.";
            break;
          case "functions/invalid-argument":
            message = error.message || "Dados inválidos para reagendamento.";
            break;
          default:
            break;
        }
      }
      setRescheduleModal((prev) => ({ ...prev, error: message, isSubmitting: false }));
    }
  };

  // Função para atualizar visibilidade pública do appointment
  const updateAppointmentPublicStatus = async (appointment: Appointment, isPublic: boolean) => {
    try {
      if (!isAppointmentConfirmed(appointment.status)) {
        setPublicUpdateError("Apenas sessões confirmadas podem ser tornadas públicas.");
        setTimeout(() => setPublicUpdateError(""), 4000);
        return;
      }

      setIsUpdatingPublic((prev) => ({ ...prev, [appointment.id]: true }));
      setPublicUpdateError("");

      const appointmentRef = doc(db, "appointments", appointment.id);
      await updateDoc(appointmentRef, { isPublic });

      // Atualiza o estado local dos appointments
      setAppointments((prev) => prev.map((item) => (item.id === appointment.id ? { ...item, isPublic } : item)));
    } catch (error: any) {
      console.error("Erro ao atualizar visibilidade pública:", error);
      setPublicUpdateError(error?.message || "Erro ao atualizar visibilidade pública.");
    } finally {
      setIsUpdatingPublic((prev) => ({ ...prev, [appointment.id]: false }));
    }
  };

  // MODIFICAÇÃO: Função centralizada para SEMPRE selecionar o avatar antes de navegar para a sala
  const proceedToRoom = (roomId: string) => {
    // Se o perfil ainda está carregando, não faz nada
    if (isProfileLoading) return;

    // Redireciona SEMPRE para a seleção de avatar, enviando uma flag para permitir a entrada na página.
    console.log("Redirecionando para seleção de avatar antes de entrar na sala.");
    navigate(`/sala/${roomId}`);
  };

  const handleEnterSession = async (appointmentId: string, terapeutaId: string) => {
    try {
      setIsConsumingHours((prev) => ({ ...prev, [appointmentId]: true }));
      setConsumeError("");
      proceedToRoom(`sala-${terapeutaId}-${appointmentId}`);
    } catch (error: any) {
      console.error("Erro ao consumir horas:", error);
      let errorMessage = "Erro desconhecido ao iniciar sessão.";
      if (error.code === "functions/failed-precondition") errorMessage = error.message || "Saldo de horas insuficiente.";
      else if (error.code === "functions/not-found") errorMessage = "Agendamento não encontrado.";
      else if (error.code === "functions/permission-denied") errorMessage = "Você não tem permissão para iniciar esta sessão.";
      else if (error.code === "functions/unauthenticated") errorMessage = "Sessão expirada. Faça login novamente.";
      else if (error.message) errorMessage = error.message;
      setConsumeError(errorMessage);
    } finally {
      setIsConsumingHours((prev) => ({ ...prev, [appointmentId]: false }));
    }
  };

  // Lógica de navegação para a sala principal do terapeuta
  const handleEnterOwnRoom = () => {
    // Verifica se tem horas suficientes
    if (!hasEnoughHours || !user?.uid) {
      return;
    }

    // A ativação da sala agora é feita automaticamente no Room.tsx
    // quando o terapeuta entra na própria sala (seja por botão ou URL direta)
    proceedToRoom(`sala-${user?.uid}`);
  };

  useEffect(() => {
    if (!user?.uid) return;

    setIsProfileLoading(true);
    const therapistRef = doc(db, "therapists", user.uid);
    const userRef = doc(db, "users", user.uid);

    // Listen to both therapist and user documents
    let therapistUnsub: (() => void) | null = null;
    let userUnsub: (() => void) | null = null;
    let latestTherapistData: any = {};
    let latestUserData: any = {};

    const updateCombinedData = () => {
      setTherapistData({
        // creditBalances always from users collection
        creditBalances: latestUserData?.creditBalances || { 
          sessions: { constellation: 0, participation: 0 }, 
          hours: 0 
        },
        horariosDisponiveis: typeof latestTherapistData?.horariosDisponiveis === "object" ? latestTherapistData.horariosDisponiveis : {},
        avatar: latestTherapistData?.avatar || "",
        pixKey: latestTherapistData?.pixKey || "",
      });
      setWorkingHours(typeof latestTherapistData?.horariosDisponiveis === "object" ? latestTherapistData.horariosDisponiveis : {});
      setPixKey(latestTherapistData?.pixKey || "");
      setPixKeyType(latestTherapistData?.pixKeyType || "CPF");
      const duration = latestTherapistData?.sessionDuration || 60;
      setSessionDuration(duration);
      const standardDurations = [60, 120, 180, 240, 300, 360];
      const isCustom = !standardDurations.includes(duration);
      setIsCustomDuration(isCustom);
      if (isCustom) {
        setCustomDuration(duration.toString());
      }
      setIsProfileLoading(false);
    };

    therapistUnsub = onSnapshot(
      therapistRef,
      (therapistDoc) => {
        latestTherapistData = therapistDoc.exists() ? therapistDoc.data() : {};
        updateCombinedData();
      },
      (error) => {
        console.error("Erro ao buscar dados do terapeuta:", error);
        latestTherapistData = {};
        updateCombinedData();
      }
    );

    userUnsub = onSnapshot(
      userRef,
      (userDoc) => {
        latestUserData = userDoc.exists() ? userDoc.data() : {};
        updateCombinedData();
      },
      (error) => {
        console.error("Erro ao buscar dados do usuário:", error);
        latestUserData = {};
        updateCombinedData();
      }
    );

    return () => {
      if (therapistUnsub) therapistUnsub();
      if (userUnsub) userUnsub();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || activeTab !== "historico") return;

    setPaymentHistoryLoading(true);
    setPaymentHistoryError("");

    const fetchHistorico = async () => {
      try {
        const q = query(
          collection(db, SPLIT_PAYMENTS_COLLECTION),
          where("therapistId", "==", user.uid),
          orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(q);
        const data: SplitPayment[] = snapshot.docs.map((d) => {
          const raw = d.data();
          return {
            id: d.id,
            therapistId: raw.therapistId ?? "",
            patientId: raw.patientId ?? "",
            patientName: raw.patientName ?? raw.patientId ?? "—",
            appointmentId: raw.appointmentId,
            appointmentDate: raw.appointmentDate?.toDate ? raw.appointmentDate.toDate() : null,
            chargeId: raw.chargeId ?? "",
            chargeValue: raw.chargeValue ?? 0,
            therapistCommission: raw.therapistCommission ?? 0,
            commissionValue: raw.commissionValue ?? 0,
            status: raw.status as SplitPaymentStatus,
            createdAt: raw.createdAt?.toDate ? raw.createdAt.toDate() : null,
          };
        });
        setPaymentHistory(data);
      } catch (err: any) {
        setPaymentHistoryError("Erro ao carregar histórico financeiro.");
        console.error(err);
      } finally {
        setPaymentHistoryLoading(false);
      }
    };

    fetchHistorico();
  }, [user?.uid, activeTab]);

  useEffect(() => {
    if (!user?.uid || activeTab !== "sessions") return;

    setIsLoading(true);
    setErrorMessage("");
    setAppointments([]);

    const fetchAppointments = async () => {
      // Buscar sessões a partir de 30 minutos atrás (para mostrar sessões recentes e em andamento)
      const thirtyMinutesAgo = new Date();
      thirtyMinutesAgo.setMinutes(0);
      thirtyMinutesAgo.setHours(0);
      thirtyMinutesAgo.setSeconds(0);

      const appointmentsQuery = query(
        collection(db, "appointments"),
        where("terapeutaId", "==", user.uid),
        where("dataSessao", ">", thirtyMinutesAgo)
      );

      const unsubscribe = onSnapshot(
        appointmentsQuery,
        async (snapshot) => {
          if (snapshot.empty) {
            setAppointments([]);
            setParticipantCounts({});
            setIsLoading(false);
            return;
          }

          const appointmentsList: Appointment[] = snapshot.docs.map(
            (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Appointment)
          );

          appointmentsList.sort((a, b) => {
            const dateA = a.dataSessao?.toDate ? a.dataSessao.toDate() : new Date();
            const dateB = b.dataSessao?.toDate ? b.dataSessao.toDate() : new Date();
            return dateA.getTime() - dateB.getTime();
          });

          setAppointments(appointmentsList);

          const countsPromises = appointmentsList.map(async (appointment) => {
            if (typeof appointment.participantCount === "number") {
              return { id: appointment.id, count: appointment.participantCount };
            }

            try {
              const participantsSnap = await getDocs(collection(db, "appointments", appointment.id, "participants"));
              const totalCount = participantsSnap.size;
              return { id: appointment.id, count: totalCount };
            } catch (error) {
              console.error(`Erro ao contar participantes para ${appointment.id}:`, error);
              return { id: appointment.id, count: 0 };
            }
          });

          const countsResults = await Promise.all(countsPromises);
          const countsMap: { [key: string]: number } = {};
          countsResults.forEach(({ id, count }) => {
            countsMap[id] = count;
          });

          setParticipantCounts(countsMap);
          setIsLoading(false);
        },
        (error) => {
          console.error("Erro ao buscar appointments:", error);
          setIsLoading(false);
          setAppointments([]);
          setParticipantCounts({});
          setErrorMessage(`Erro ao carregar as sessões: ${error.message || "Erro desconhecido"}.`);
        }
      );
      return unsubscribe;
    };

    let unsubscribeFunction: (() => void) | null = null;
    fetchAppointments().then((unsubscribe) => {
      unsubscribeFunction = unsubscribe;
    });

    return () => {
      if (unsubscribeFunction) unsubscribeFunction();
    };
  }, [user?.uid, activeTab]);

  // Escuta mensagens não lidas em tempo real
  useEffect(() => {
    if (!user?.uid || appointments.length === 0) return;

    const unsubscribers: (() => void)[] = [];

    // Para cada appointment, cria um chatId e escuta as mensagens não lidas
    appointments.forEach((appointment) => {
      // Chat com o paciente
      const chatIdWithPatient = `${user.uid}-${appointment.pacienteId}`;
      const chatRefWithPatient = doc(db, 'chats', chatIdWithPatient);
      
      const unsubscribePatient = onSnapshot(chatRefWithPatient, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const unreadCount = data.unreadCount?.[user.uid] || 0;
          setUnreadMessages(prev => ({
            ...prev,
            [chatIdWithPatient]: unreadCount
          }));
        }
      });
      
      unsubscribers.push(unsubscribePatient);

      // Chat com cada participante (observadores)
      if (appointmentParticipants[appointment.id]) {
        appointmentParticipants[appointment.id].forEach((participant: any) => {
          const chatIdWithObserver = `${user.uid}-${participant.id}`;
          const chatRefWithObserver = doc(db, 'chats', chatIdWithObserver);
          
          const unsubscribeObserver = onSnapshot(chatRefWithObserver, (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.data();
              const unreadCount = data.unreadCount?.[user.uid] || 0;
              setUnreadMessages(prev => ({
                ...prev,
                [chatIdWithObserver]: unreadCount
              }));
            }
          });
          
          unsubscribers.push(unsubscribeObserver);
        });
      }
    });

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [user?.uid, appointments, appointmentParticipants]);

  const updateWorkingHours = async () => {
    if (!user?.uid) {
      setWorkingHoursSaveStatus("error");
      setTimeout(() => setWorkingHoursSaveStatus("idle"), 4000);
      return;
    }
    try {
      const therapistRef = doc(db, "therapists", user.uid);
      await updateDoc(therapistRef, { horariosDisponiveis: workingHours });
      setWorkingHoursSaveStatus("success");
      setTimeout(() => setWorkingHoursSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Erro ao atualizar horários:", error);
      setWorkingHoursSaveStatus("error");
      setTimeout(() => setWorkingHoursSaveStatus("idle"), 4000);
    }
  };

  const validatePixKey = (key: string, type: PixKeyType): { valid: boolean; error: string } => {
    if (!key.trim()) {
      return { valid: false, error: "Por favor, informe a chave PIX." };
    }

    const cleanKey = key.replace(/\D/g, "");

    switch (type) {
      case PIX_KEY_TYPES.CPF:
        if (cleanKey.length !== 11) {
          return { valid: false, error: "CPF deve ter 11 dígitos." };
        }
        break;

      case PIX_KEY_TYPES.CNPJ:
        if (cleanKey.length !== 14) {
          return { valid: false, error: "CNPJ deve ter 14 dígitos." };
        }
        break;

      case PIX_KEY_TYPES.EMAIL:
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(key)) {
          return { valid: false, error: "Por favor, informe um e-mail válido." };
        }
        break;

      case PIX_KEY_TYPES.PHONE:
        if (cleanKey.length !== 11) {
          return { valid: false, error: "Telefone deve ter 11 dígitos (DDD + número)." };
        }
        break;

      case PIX_KEY_TYPES.EVP:
        if (key.length < 32) {
          return { valid: false, error: "Chave aleatória inválida. Deve ter pelo menos 32 caracteres." };
        }
        break;

      default:
        return { valid: false, error: "Tipo de chave inválido." };
    }

    return { valid: true, error: "" };
  };

  const updatePixKey = async () => {
    if (!user?.uid) {
      setPixSaveStatus("error");
      setPixErrorMessage("Erro: Utilizador não autenticado");
      setTimeout(() => {
        setPixSaveStatus("idle");
        setPixErrorMessage("");
      }, 4000);
      return;
    }

    const validation = validatePixKey(pixKey, pixKeyType);
    if (!validation.valid) {
      setPixSaveStatus("error");
      setPixErrorMessage(validation.error);
      setTimeout(() => {
        setPixSaveStatus("idle");
        setPixErrorMessage("");
      }, 4000);
      return;
    }

    setPixSaving(true);
    setPixSaveStatus("idle");
    setPixErrorMessage("");
    try {
      const therapistRef = doc(db, "therapists", user.uid);
      // Usa setDoc com merge para garantir criação do documento se necessário
      await setDoc(therapistRef, { pixKey, pixKeyType }, { merge: true });
      setPixSaveStatus("success");
      setTimeout(() => setPixSaveStatus("idle"), 3000);
    } catch (error: any) {
      console.error("Erro ao atualizar chave PIX:", error);
      setPixSaveStatus("error");
      setPixErrorMessage(error?.message || "Erro ao atualizar chave PIX. Tente novamente.");
      setTimeout(() => {
        setPixSaveStatus("idle");
        setPixErrorMessage("");
      }, 4000);
    } finally {
      setPixSaving(false);
    }
  };

  const updateSessionDuration = async () => {
    if (!user?.uid) {
      setDurationSaveStatus("error");
      setDurationErrorMessage("Erro: Utilizador não autenticado");
      setTimeout(() => {
        setDurationSaveStatus("idle");
        setDurationErrorMessage("");
      }, 4000);
      return;
    }

    // Validação para duração personalizada
    if (isCustomDuration) {
      const customValue = parseInt(customDuration);
      if (isNaN(customValue) || customValue < 15 || customValue > 600) {
        setDurationSaveStatus("error");
        setDurationErrorMessage("Por favor, insira uma duração entre 15 e 600 minutos.");
        setTimeout(() => {
          setDurationSaveStatus("idle");
          setDurationErrorMessage("");
        }, 4000);
        return;
      }
    }

    setDurationSaving(true);
    setDurationSaveStatus("idle");
    setDurationErrorMessage("");
    try {
      const therapistRef = doc(db, "therapists", user.uid);
      const finalDuration = isCustomDuration ? parseInt(customDuration) : sessionDuration;
      await setDoc(therapistRef, { sessionDuration: finalDuration }, { merge: true });
      setDurationSaveStatus("success");
      setTimeout(() => setDurationSaveStatus("idle"), 3000);
    } catch (error: any) {
      console.error("Erro ao atualizar duração da sessão:", error);
      setDurationSaveStatus("error");
      setDurationErrorMessage(error?.message || "Erro ao atualizar duração da sessão. Tente novamente.");
      setTimeout(() => {
        setDurationSaveStatus("idle");
        setDurationErrorMessage("");
      }, 4000);
    } finally {
      setDurationSaving(false);
    }
  };

  const handleDurationChange = (value: string) => {
    if (value === "custom") {
      setIsCustomDuration(true);
      setCustomDuration("");
    } else {
      setIsCustomDuration(false);
      setSessionDuration(parseInt(value));
      setCustomDuration("");
    }
  };

  const daysOfWeek = [
    { key: "monday", label: "Segunda-feira" },
    { key: "tuesday", label: "Terça-feira" },
    { key: "wednesday", label: "Quarta-feira" },
    { key: "thursday", label: "Quinta-feira" },
    { key: "friday", label: "Sexta-feira" },
    { key: "saturday", label: "Sábado" },
    { key: "sunday", label: "Domingo" },
  ];

  const addTimeSlot = (dayKey: string) => {
    const updatedHours = { ...workingHours };
    if (!updatedHours[dayKey]) updatedHours[dayKey] = [];
    updatedHours[dayKey].push({ inicio: "09:00", fim: "17:00" });
    setWorkingHours(updatedHours);
  };

  const updateTimeSlot = (dayKey: string, slotIndex: number, field: "inicio" | "fim", value: string) => {
    const updatedHours = { ...workingHours };
    if (!updatedHours[dayKey]) updatedHours[dayKey] = [];
    updatedHours[dayKey][slotIndex] = { ...updatedHours[dayKey][slotIndex], [field]: value };
    setWorkingHours(updatedHours);
  };

  const removeTimeSlot = (dayKey: string, slotIndex: number) => {
    const updatedHours = { ...workingHours };
    if (updatedHours[dayKey]) {
      updatedHours[dayKey].splice(slotIndex, 1);
      if (updatedHours[dayKey].length === 0) delete updatedHours[dayKey];
      setWorkingHours(updatedHours);
    }
  };

  const handleRetry = () => {
    setErrorMessage("");
    setIsLoading(true);
    const currentTab = activeTab;
    setActiveTab(activeTab === "sessions" ? "hours" : "sessions"); // Alterna para forçar re-render
    setTimeout(() => setActiveTab(currentTab), 50);
  };

  const tabs = [
    { id: "sessions", label: "Minhas Sessões", icon: Calendar },
    { id: "perfil", label: "Editar Perfil", icon: User },
    { id: "hours", label: "Meus Créditos", icon: Clock },
    { id: "subscriptions", label: "Assinaturas", icon: CreditCard },
    { id: "historico", label: "Histórico Financeiro", icon: DollarSign },
    { id: "settings", label: "Configurações", icon: Settings },
  ] as const;

  return (
    <div className="space-y-6 relative">
      <div className="flex items-center justify-between pt-5">
        <h1 className="text-2xl font-bold text-gray-900">Painel do Terapeuta</h1>

        {/* BOTÃO MODIFICADO: De Link para button para controlar a navegação */}
        <button
          onClick={handleEnterOwnRoom}
          disabled={isProfileLoading} // Desabilitado enquanto o perfil carrega
          className={`px-6 py-3 rounded-lg font-medium shadow-md transition-colors flex items-center gap-2 ${hasEnoughHours && !isProfileLoading
            ? "bg-green-600 text-white hover:bg-green-700 hover:shadow-lg"
            : "bg-gray-400 text-white cursor-not-allowed"
            }`}
          title={
            isProfileLoading
              ? "Verificando perfil..."
              : hasEnoughHours
                ? "Entrar na sua sala de sessão"
                : "Você precisa de mais horas para iniciar uma sessão"
          }>
          {isProfileLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Video className="w-5 h-5" />}
          Entrar na Minha Sala
        </button>
      </div>

      {!hasEnoughHours && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                Você precisa de mais horas disponíveis para iniciar uma sessão.{" "}
                <span className="font-medium"> Compre mais horas na aba "Meus Créditos".</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {consumeError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{consumeError}</p>
              <button onClick={() => setConsumeError("")} className="mt-2 text-xs text-red-600 underline hover:text-red-800">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}>
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        {activeTab === "sessions" && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Próximas Sessões</h2>
            {errorMessage && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-red-800 font-medium">Erro</span>
                </div>
                <p className="text-red-700 text-sm mt-2">{errorMessage}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={handleRetry} className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200">
                    Tentar Novamente
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">
                    Recarregar Página
                  </button>
                </div>
              </div>
            )}
            {rescheduleFeedback && (
              <div
                className={`mb-4 p-4 rounded-lg border flex items-start justify-between gap-4 ${rescheduleFeedback.type === "success"
                  ? "bg-green-50 border-green-200"
                  : "bg-red-50 border-red-200"
                  }`}
              >
                <div>
                  <p
                    className={`text-sm font-medium ${rescheduleFeedback.type === "success" ? "text-green-700" : "text-red-700"
                      }`}
                  >
                    {rescheduleFeedback.message}
                  </p>
                </div>
                <button
                  type="button"
                  className="text-xs font-medium text-gray-500 hover:text-gray-700"
                  onClick={dismissRescheduleFeedback}
                >
                  Fechar
                </button>
              </div>
            )}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="ml-2">Carregando...</span>
              </div>
            ) : !errorMessage && appointments.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">Nenhuma sessão agendada</p>
              </div>
            ) : !errorMessage && appointments.length > 0 ? (
              <div className="space-y-3">
                {appointments.map((appointment) => {
                  const sessionTime = appointment.dataSessao?.toDate ? appointment.dataSessao.toDate() : new Date();
                  const today = new Date();
                  today.setSeconds(0);
                  today.setHours(0);
                  today.setMinutes(0);
                  const minutesDiff = (sessionTime.getTime() - today.getTime()) / 60000;
                  const canJoin = sessionTime >= today && minutesDiff >= -30;
                  const isActive = minutesDiff <= 0 && minutesDiff >= -60;
                  const isLoadingThisSession = isConsumingHours[appointment.id] || false;
                  const registeredCount = participantCounts[appointment.id] || 0;

                  return (
                    <div
                      key={appointment.id}
                      className={`border rounded-lg p-4 ${isActive ? "border-green-300 bg-green-50" : "border-gray-200 bg-white"} shadow-sm hover:shadow-md transition-shadow`}>
                      
                      <div className="flex flex-col md:flex-row gap-4">
                        {/* Lado Esquerdo: Informações da Sessão */}
                        <div className="flex-1 min-w-0 space-y-2">
                          {/* Nome e Status */}
                          <div className="flex items-start gap-2">
                            <h3 className="font-semibold text-base text-gray-900 flex-1 truncate">{appointment.pacienteNome || "Nome não disponível"}</h3>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${isAppointmentConfirmed(appointment.status)
                                ? "bg-green-100 text-green-800"
                                : appointment.status === "cancelado"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}>
                              {appointment.status || "pendente"}
                            </span>
                          </div>

                          {/* Data e Participantes */}
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {sessionTime.toLocaleDateString("pt-BR", { day: '2-digit', month: 'short' })} às{" "}
                              {sessionTime.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                            {registeredCount > 0 && (
                              <>
                                <span className="text-gray-400">•</span>
                                <button
                                  onClick={() => toggleParticipantsList(appointment.id)}
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                  <Users className="w-3.5 h-3.5" />
                                  {registeredCount} participante{registeredCount !== 1 ? "s" : ""}
                                  {expandedParticipants[appointment.id] ? " ▼" : " ▶"}
                                </button>
                              </>
                            )}
                          </div>

                          {/* Lista de Participantes Expansível */}
                          {expandedParticipants[appointment.id] && (
                            <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                              {loadingParticipants[appointment.id] ? (
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Carregando participantes...
                                </div>
                              ) : appointmentParticipants[appointment.id]?.length > 0 ? (
                                <div className="space-y-2">
                                  <p className="text-xs font-medium text-gray-700">Observadores registrados:</p>
                                  {appointmentParticipants[appointment.id].map((participant: any) => (
                                    <div key={participant.id} className="flex items-center justify-between gap-2 p-2 bg-white rounded border border-gray-100">
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-gray-900 truncate">{participant.name}</p>
                                        {participant.email && (
                                          <p className="text-xs text-gray-500 truncate">{participant.email}</p>
                                        )}
                                      </div>
                                      <ChatButton 
                                        label="Chat"
                                        unreadCount={unreadMessages[`${user?.uid}-${participant.id}`] || 0}
                                        onClick={() => setOpenChat({
                                          therapistId: user?.uid || '',
                                          therapistName: user?.name || user?.email || 'Você',
                                          otherMemberId: participant.id,
                                          otherMemberName: participant.name,
                                          otherMemberRole: ParticipantRole.OBSERVADOR,
                                        })}
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-500">Nenhum participante registrado ainda.</p>
                              )}
                            </div>
                          )}

                          {/* Switch de Visibilidade */}
                          <div>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={appointment.isPublic || false}
                                onChange={(e) => updateAppointmentPublicStatus(appointment, e.target.checked)}
                                disabled={isUpdatingPublic[appointment.id] || !isAppointmentConfirmed(appointment.status)}
                                className="sr-only"
                              />
                              <div
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${appointment.isPublic ? "bg-blue-600" : "bg-gray-300"
                                  } ${isUpdatingPublic[appointment.id] || appointment.status !== "confirmado"
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                  }`}>
                                <span
                                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${appointment.isPublic ? "translate-x-5" : "translate-x-1"
                                    }`}
                                />
                              </div>
                              <span className="ml-2 text-xs text-gray-700">
                                {!isAppointmentConfirmed(appointment.status) ? (
                                  "Disponível após confirmação"
                                ) : isUpdatingPublic[appointment.id] ? (
                                  <span className="flex items-center gap-1">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Atualizando...
                                  </span>
                                ) : appointment.isPublic ? (
                                  "Sala pública"
                                ) : (
                                  "Sala privada"
                                )}
                              </span>
                            </label>
                            {publicUpdateError && <p className="text-xs text-red-600 mt-1">{publicUpdateError}</p>}
                          </div>
                        </div>

                        {/* Lado Direito: Ações */}
                        <div className="flex flex-col justify-center gap-2 md:min-w-[280px]">
                          {isAppointmentConfirmed(appointment.status) && (
                            <ChatButton 
                              label="Falar com Constelado" 
                              unreadCount={unreadMessages[`${user?.uid}-${appointment.pacienteId}`] || 0}
                              onClick={() => setOpenChat({
                                therapistId: user?.uid || '',
                                therapistName: user?.name || user?.email || 'Você',
                                otherMemberId: appointment.pacienteId,
                                otherMemberName: appointment.pacienteNome,
                                otherMemberRole: ParticipantRole.CONSTELADO,
                              })}
                            />
                          )}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => openRescheduleModal(appointment)}
                              className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60 flex items-center justify-center gap-1.5"
                              disabled={!isAppointmentConfirmed(appointment.status)}
                            >
                              <Calendar className="w-3.5 h-3.5" />
                              Reagendar
                            </button>
                            {canJoin && isAppointmentConfirmed(appointment.status) && (
                              <button
                                onClick={() => handleEnterSession(appointment.id, appointment.terapeutaId)}
                                disabled={!hasEnoughHours || isLoadingThisSession || isProfileLoading}
                                className={`flex-1 px-3 py-1.5 text-xs rounded-lg font-medium transition-colors flex items-center justify-center gap-1.5 ${!hasEnoughHours || isProfileLoading
                                  ? "bg-gray-400 text-white cursor-not-allowed"
                                  : isLoadingThisSession
                                    ? "bg-gray-400 text-white cursor-wait"
                                    : isActive
                                      ? "bg-green-600 hover:bg-green-700 text-white"
                                      : "bg-blue-600 hover:bg-blue-700 text-white"
                                }`}
                                title={
                                  isProfileLoading
                                    ? "Verificando perfil..."
                                    : !hasEnoughHours
                                      ? "Você precisa de mais horas"
                                      : isLoadingThisSession
                                        ? "Processando..."
                                        : "Entrar na sessão"
                                }>
                                {isLoadingThisSession || isProfileLoading ? (
                                  <>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Processando...
                                  </>
                                ) : (
                                  <>
                                    <Video className="w-3.5 h-3.5" /> {isActive ? "Entrar Agora" : "Entrar"}
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        )}
        {activeTab === "hours" && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Gestão de Créditos</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Card de Horas */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6 text-blue-600" />
                  <h3 className="text-lg font-medium text-blue-900">Saldo de Tempo</h3>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600 mb-2">{getTimeBalanceLabel()}</p>
                  <p className="text-blue-700 text-sm">disponíveis</p>
                </div>
                <button
                  onClick={() => navigate("/planos?secao=terapeuta")}
                  className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Comprar Mais Horas
                </button>

                {hasEnoughHours ? (
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-700 text-sm font-medium">Suficiente para sessões</span>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-500" />
                      <span className="text-orange-700 text-sm font-medium">Insuficiente para sessões</span>
                    </div>
                  )}
              </div>

              {/* Card de Constelações */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Star className="w-6 h-6 text-purple-600" />
                  <h3 className="text-lg font-medium text-purple-900">Constelações</h3>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-600 mb-2">
                    {therapistData.creditBalances?.sessions?.constellation ?? 0}
                  </p>
                  <p className="text-purple-700 text-sm">sessões disponíveis</p>
                </div>
                <button
                  onClick={() => navigate("/planos?secao=terapeuta&tipo=constellation")}
                  className="w-full mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Comprar Constelações
                </button>
              </div>

              {/* Card de Participações */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-medium text-green-900">Participações</h3>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600 mb-2">
                    {therapistData.creditBalances?.sessions?.participation ?? 0}
                  </p>
                  <p className="text-green-700 text-sm">sessões disponíveis</p>
                </div>
                <button
                  onClick={() => navigate("/planos?secao=terapeuta&tipo=participation")}
                  className="w-full mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Comprar Participações
                </button>
              </div>
            </div>
            
            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-2">Informações sobre créditos</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• <strong>Horas:</strong> Necessárias para você iniciar e conduzir outras sessões</li>
                <li>• <strong>Constelações:</strong> Sessões para fazer a sua própria constelação familiar conduzida por outro terapeuta</li>
                <li>• <strong>Participações:</strong> Sessões onde você participa como observador em constelações de outros terapeutas</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === "perfil" && (
          <div className="p-6">
            <TherapistProfile user={user} />
          </div>
        )}

        {activeTab === "subscriptions" && (
          <div className="p-6">
            <ActiveSubscriptionsPanel />
          </div>
        )}

        {activeTab === "historico" && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Histórico Financeiro</h2>
            <p className="text-sm text-gray-500 mb-2">Repasses recebidos pelas sessões realizadas.</p>

            {/* Filtro de data */}
            <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">De</label>
                <input
                  type="date"
                  value={paymentHistoryDateFrom}
                  onChange={(e) => setPaymentHistoryDateFrom(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Até</label>
                <input
                  type="date"
                  value={paymentHistoryDateTo}
                  onChange={(e) => setPaymentHistoryDateTo(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {(paymentHistoryDateFrom || paymentHistoryDateTo) && (
                <button
                  onClick={() => { setPaymentHistoryDateFrom(""); setPaymentHistoryDateTo(""); }}
                  className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Limpar filtro
                </button>
              )}
            </div>

            {paymentHistoryLoading && (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Carregando histórico...</span>
              </div>
            )}

            {paymentHistoryError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {paymentHistoryError}
              </div>
            )}

            {!paymentHistoryLoading && !paymentHistoryError && paymentHistory.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum repasse encontrado.</p>
              </div>
            )}

            {!paymentHistoryLoading && paymentHistory.length > 0 && (() => {
              const filteredHistory = paymentHistory.filter((sp) => {
                const date = sp.appointmentDate;
                if (!date) return true;
                if (paymentHistoryDateFrom) {
                  const from = new Date(paymentHistoryDateFrom + "T00:00:00");
                  if (date < from) return false;
                }
                if (paymentHistoryDateTo) {
                  const to = new Date(paymentHistoryDateTo + "T23:59:59");
                  if (date > to) return false;
                }
                return true;
              });
              return (
              <div className="overflow-x-auto">
                {filteredHistory.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nenhum repasse encontrado para o período selecionado.</p>
                  </div>
                ) : (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paciente</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Valor da Sessão</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Comissão</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Repasse</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredHistory.map((sp) => (
                      <tr key={sp.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                          {sp.appointmentDate
                            ? sp.appointmentDate.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{sp.patientName}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap text-gray-700">
                          {sp.chargeValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap text-gray-500">
                          {(sp.therapistCommission).toFixed(0)}%
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap font-semibold text-green-700">
                          {sp.commissionValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            SPLIT_PAYMENT_STATUS_COLORS[sp.status] ?? "bg-gray-100 text-gray-600"
                          }`}>
                            {SPLIT_PAYMENT_STATUS_LABELS[sp.status] ?? sp.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-right text-sm font-medium text-gray-600">Total recebido (Pago):</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-green-700">
                        {filteredHistory
                          .filter((sp) => sp.status === SplitPaymentStatus.COMPLETED)
                          .reduce((acc, sp) => acc + sp.commissionValue, 0)
                          .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
                )}
              </div>
              );
            })()}
          </div>
        )}

        {activeTab === "settings" && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Configurações</h2>
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Integração com Google Agenda</h3>
                <GoogleCalendarSettings />
              </div>
           
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Duração da Sessão</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Duração padrão das sessões (em minutos)</label>
                      <div className="flex items-center gap-4">
                        <select
                          value={isCustomDuration ? "custom" : sessionDuration}
                          onChange={(e) => handleDurationChange(e.target.value)}
                          className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                          <option value={60}>1 hora</option>
                          <option value={120}>2 horas</option>
                          <option value={180}>3 horas</option>
                          <option value={240}>4 horas</option>
                          <option value={300}>5 horas</option>
                          <option value={360}>6 horas</option>
                          <option value="custom">Outros</option>
                        </select>
                        {isCustomDuration && (
                          <input
                            type="number"
                            value={customDuration}
                            onChange={(e) => setCustomDuration(e.target.value)}
                            placeholder="Minutos"
                            min="15"
                            max="600"
                            className="block w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        )}
                        <button
                          onClick={updateSessionDuration}
                          disabled={durationSaving}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2">
                          {durationSaving ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Salvando...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              Salvar
                            </>
                          )}
                        </button>
                      </div>

                      {/* Mensagens de feedback para duração da sessão */}
                      {durationSaveStatus === "success" && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-green-800 text-sm font-medium">Duração da sessão atualizada com sucesso!</span>
                        </div>
                      )}

                      {durationSaveStatus === "error" && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-600" />
                          <div>
                            <span className="text-red-800 text-sm font-medium">Erro ao salvar</span>
                            {durationErrorMessage && <p className="text-red-700 text-sm mt-1">{durationErrorMessage}</p>}
                          </div>
                        </div>
                      )}

                      <p className="text-xs text-gray-500 mt-2">
                        Esta configuração define a duração padrão das suas sessões de terapia.
                        {isCustomDuration && " Para duração personalizada, insira um valor entre 15 e 600 minutos."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Horários de Trabalho</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-6">
                    {daysOfWeek.map((day) => (
                      <div key={day.key} className="border-b border-gray-200 pb-4 last:border-b-0">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-700">{day.label}</h4>
                          <button
                            onClick={() => addTimeSlot(day.key)}
                            className="px-3 py-1 text-blue-600 border border-blue-300 rounded text-sm hover:bg-blue-50 flex items-center gap-1">
                            <Plus className="w-3 h-3" />
                            Adicionar
                          </button>
                        </div>
                        {workingHours[day.key] && workingHours[day.key].length > 0 ? (
                          <div className="space-y-2">
                            {workingHours[day.key].map((timeSlot, slotIndex) => (
                              <div key={slotIndex} className="flex items-center gap-2 bg-white p-3 rounded border">
                                <span className="text-sm text-gray-600 w-8">De:</span>
                                <input
                                  type="time"
                                  value={timeSlot.inicio}
                                  onChange={(e) => updateTimeSlot(day.key, slotIndex, "inicio", e.target.value)}
                                  className="px-2 py-1 border rounded text-sm"
                                />
                                <span className="text-sm text-gray-600">até:</span>
                                <input
                                  type="time"
                                  value={timeSlot.fim}
                                  onChange={(e) => updateTimeSlot(day.key, slotIndex, "fim", e.target.value)}
                                  className="px-2 py-1 border rounded text-sm"
                                />
                                <button
                                  onClick={() => removeTimeSlot(day.key, slotIndex)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded">
                                  <UserX className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-gray-500 text-sm bg-white rounded border border-dashed">
                            Nenhum horário definido
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="pt-4 border-t border-gray-200 space-y-3">
                      <button
                        onClick={updateWorkingHours}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                        Salvar Horários
                      </button>

                      {/* Mensagens de feedback para horários de trabalho */}
                      {workingHoursSaveStatus === "success" && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-green-800 text-sm font-medium">Horários atualizados com sucesso!</span>
                        </div>
                      )}

                      {workingHoursSaveStatus === "error" && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-600" />
                          <span className="text-red-800 text-sm font-medium">Erro ao atualizar horários. Tente novamente.</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Configurações de Pagamento</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Chave PIX</label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <select
                          value={pixKeyType}
                          onChange={(e) => setPixKeyType(e.target.value as PixKeyType)}
                          className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm w-full sm:w-40">
                          {Object.entries(PIX_KEY_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                        </select>
                        
                        <TextInput
                          value={pixKey}
                          onChange={(e) => setPixKey(e.target.value)}
                          placeholder={PIX_KEY_PLACEHOLDERS[pixKeyType]}
                          className="flex-1"
                        />
                        
                        <button
                          onClick={updatePixKey}
                          disabled={pixSaving}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap">
                          {pixSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                          <span>Salvar</span>
                        </button>
                      </div>
                      <div className="mt-2">
                        <p className="text-xs text-gray-500">
                          {pixKeyType === PIX_KEY_TYPES.CPF && "Digite apenas os 11 dígitos do CPF. "}
                          {pixKeyType === PIX_KEY_TYPES.CNPJ && "Digite apenas os 14 dígitos do CNPJ. "}
                          {pixKeyType === PIX_KEY_TYPES.EMAIL && "Digite um endereço de e-mail válido. "}
                          {pixKeyType === PIX_KEY_TYPES.PHONE && "Digite o telefone com DDD (11 dígitos). "}
                          {pixKeyType === PIX_KEY_TYPES.EVP && "Cole a chave aleatória fornecida pelo seu banco. "}
                          Esta chave será usada para receber pagamentos dos pacientes.
                        </p>
                        {pixSaveStatus === "success" && (
                          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-xs text-green-800 font-medium">Chave PIX salva com sucesso.</span>
                          </div>
                        )}
                        {pixSaveStatus === "error" && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <span className="text-xs text-red-800 font-medium block">Erro ao salvar a chave PIX.</span>
                              {pixErrorMessage && <span className="text-xs text-red-700">{pixErrorMessage}</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {rescheduleModal.appointmentId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-5 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Reagendar sessão</h3>
                  <p className="text-sm text-gray-500">Escolha um novo dia e horário para este atendimento.</p>
                </div>
                <button
                  type="button"
                  onClick={closeRescheduleModal}
                  className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Fechar"
                  disabled={rescheduleModal.isSubmitting}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <form onSubmit={handleRescheduleSubmit} className="space-y-5 px-5 py-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Nova data e hora</label>
                  <input
                    type="datetime-local"
                    value={rescheduleModal.dateTime}
                    onChange={(event) =>
                      setRescheduleModal((prev) => ({ ...prev, dateTime: event.target.value, error: "" }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="mt-2 text-xs text-gray-500">O horário será sincronizado para o fuso horário de Brasília.</p>
                </div>

                {rescheduleModal.error && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertCircle className="mt-0.5 h-4 w-4" />
                    <span>{rescheduleModal.error}</span>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeRescheduleModal}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={rescheduleModal.isSubmitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={rescheduleModal.isSubmitting}
                  >
                    {rescheduleModal.isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Salvando
                      </>
                    ) : (
                      "Confirmar"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Chat */}
      {openChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-2xl lg:h-[600px]">
            <button
              onClick={() => setOpenChat(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              aria-label="Fechar chat"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <Chat 
              therapistId={openChat.therapistId}
              therapistName={openChat.therapistName}
              otherMemberId={openChat.otherMemberId}
              otherMemberName={openChat.otherMemberName}
              otherMemberRole={openChat.otherMemberRole}
              currentUserId={user?.uid || ''}
              currentUserName={user?.name || user?.email || 'Você'}
              currentUserRole={user?.uid === openChat.therapistId ? ParticipantRole.TERAPEUTA : openChat.otherMemberRole}
            />
          </div>
        </div>
      )}

      {/* Widget de Chat Flutuante */}
      <ChatWidget 
        currentUserId={user?.uid || ''}
        currentUserName={user?.name || user?.email || 'Você'}
        currentUserRole={ParticipantRole.TERAPEUTA}
        isTherapist={true}
      />
    </div>
  );
};

// Sub-componente: Painel do Paciente

// Interface para salas convidadas
interface InvitedRoom {
  id: string;
  name?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: Date;
  isActive: boolean;
  invitedUsers: string[];
}

const PatientDashboard: React.FC = () => {
  const { user } = useAuth();
  const { canParticipateInSessions, hasActiveClubeAccess, representanteUsesLeft, clubeExpiresAt } = usePlans();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"sessions" | "subscriptions" | "credits">("sessions");
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [publicSessions, setPublicSessions] = useState<Appointment[]>([]);
  const [invitedRooms, setInvitedRooms] = useState<InvitedRoom[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [therapistSessionDurations, setTherapistSessionDurations] = useState<{ [key: string]: number }>({});
  const [creditBalances, setCreditBalances] = useState<{
    sessions: {
      constellation: number;
      participation: number;
    };
    hours: number;
  }>({
    sessions: {
      constellation: 0,
      participation: 0,
    },
    hours: 0,
  });
  
  // Estado para controlar o chat aberto
  const [openChat, setOpenChat] = useState<{
    therapistId: string;
    therapistName: string;
    otherMemberId: string;
    otherMemberName: string;
    otherMemberRole: ParticipantRole;
  } | null>(null);

  // Estado para rastrear mensagens não lidas
  const [unreadMessages, setUnreadMessages] = useState<{ [chatId: string]: number }>({});

  const [userParticipations, setUserParticipations] = useState<Set<string>>(new Set());
  const [scheduledParticipationsSet] = useUserParticipations(user?.uid);
  const [scheduledParticipations, setScheduledParticipations] = useState<Appointment[]>([]);

  const isAppointmentConfirmed = (status: string) => {
    return status === AppointmentStatus.CONFIRMED;
  };

  const getSessionEndTime = (sessionDate: Date): Date => {
    const endTime = new Date(sessionDate);
    endTime.setMinutes(endTime.getMinutes() + 30);
    return endTime;
  };

  const safeGetDate = (dateSource: any): Date => {
    if (dateSource && typeof dateSource.toDate === "function") {
      return dateSource.toDate();
    }
    return new Date(dateSource);
  };

  const handleScheduleConstellation = () => {
    navigate("/terapeutas");
  };

  useEffect(() => {
    if (!user?.uid) {
      setIsLoading(false);
      return;
    }

    const thirtyMinutesAgo = new Date();
    thirtyMinutesAgo.setMinutes(0);
    thirtyMinutesAgo.setHours(0);
    thirtyMinutesAgo.setSeconds(0);

    const appointmentsQuery = query(
      collection(db, "appointments"),
      where("pacienteId", "==", user.uid),
      where("dataSessao", ">", thirtyMinutesAgo)
    );

    const unsubscribeAppointments = onSnapshot(
      appointmentsQuery,
      async (snapshot) => {
        const appointmentsList: Appointment[] = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Appointment));
        appointmentsList.sort((a, b) => a.dataSessao.toDate().getTime() - b.dataSessao.toDate().getTime());

        let nextSessionFound = false;
        appointmentsList.forEach((apt) => {
          apt.isNextSession = false;
          if (!nextSessionFound && apt.dataSessao.toDate() >= new Date()) {
            apt.isNextSession = true;
            nextSessionFound = true;
          }
        });

        setAppointments(appointmentsList);

        // Buscar durações das sessões dos terapeutas
        const therapistIdsSet = new Set(appointmentsList.map((apt) => apt.terapeutaId));
        const therapistIds = Array.from(therapistIdsSet);
        const durationsMap: { [key: string]: number } = {};

        for (const therapistId of therapistIds) {
          try {
            const therapistRef = doc(db, "therapists", therapistId);
            const therapistSnap = await getDoc(therapistRef);
            if (therapistSnap.exists()) {
              const data = therapistSnap.data();
              durationsMap[therapistId] = data?.sessionDuration || 60;
            } else {
              durationsMap[therapistId] = 60; // Default 60 minutos
            }
          } catch (error) {
            console.error(`Erro ao buscar duração da sessão do terapeuta ${therapistId}:`, error);
            durationsMap[therapistId] = 60; // Default 60 minutos
          }
        }

        setTherapistSessionDurations(durationsMap);
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      }
    );

    return () => unsubscribeAppointments();
  }, [user?.uid]);

  // Buscar créditos do usuário
  useEffect(() => {
    if (!user?.uid) return;

    const userRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setCreditBalances({
          sessions: {
            constellation: data?.creditBalances?.sessions?.constellation || 0,
            participation: data?.creditBalances?.sessions?.participation || 0,
          },
          hours: data?.creditBalances?.hours || 0,
        });
      }
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Buscar salas onde o usuário foi convidado
  useEffect(() => {
    if (!user?.uid) {
      setInvitedRooms([]);
      return;
    }

    const invitedRoomsQuery = query(
      collection(db, "rooms"),
      where("isActive", "==", true),
      where("invitedUsers", "array-contains", user.uid)
    );

    const unsubscribeInvitedRooms = onSnapshot(
      invitedRoomsQuery,
      async (snapshot) => {
        // Processa cada sala e verifica se o usuário já é participante com role 'paciente'
        const roomsPromises = snapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data();

          // Verifica participantes da sala e se o usuário já é participante com role 'paciente'
          try {
            const participantsQuery = query(collection(db, "rooms", docSnapshot.id, "participants"));
            const participantsSnapshot = await getDocs(participantsQuery);

            // Se não há participantes na sala, não inclui na lista
            if (participantsSnapshot.empty) {
              return null;
            }

            // Verifica se o usuário convidado já é participante com role 'paciente'
            const userParticipant = participantsSnapshot.docs.find((doc) => doc.id === user.uid);
            if (userParticipant) {
              const participantData = userParticipant.data();
              // Se o usuário já é participante com role 'paciente', não inclui na lista
              if (participantData.role === "paciente") {
                return null;
              }
            }
          } catch (error) {
            // Se houver erro ao verificar participantes, não inclui a sala por segurança
            console.warn(`Erro ao verificar participantes da sala ${docSnapshot.id}:`, error);
            return null;
          }

          // Busca o nome público do terapeuta
          const createdByName = await getUserNameFromRoomId(docSnapshot.id);

          return {
            id: docSnapshot.id,
            name: data.name,
            createdBy: data.createdBy,
            createdByName,
            createdAt: data.createdAt?.toDate() || new Date(),
            isActive: data.isActive || false,
            invitedUsers: data.invitedUsers || [],
          } as InvitedRoom;
        });

        const allRooms = await Promise.all(roomsPromises);
        // Filtra salas nulas (onde o usuário já é participante)
        const roomsList = allRooms.filter((room) => room !== null) as InvitedRoom[];

        // Ordena por data de criação (mais recentes primeiro)
        roomsList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setInvitedRooms(roomsList);
      },
      (error) => {
        console.error("Erro ao buscar salas convidadas:", error);
        setInvitedRooms([]);
      }
    );

    return () => unsubscribeInvitedRooms();
  }, [user?.uid]);

  // Escuta mensagens não lidas em tempo real
  useEffect(() => {
    if (!user?.uid || appointments.length === 0) return;

    const unsubscribers: (() => void)[] = [];

    // Para cada appointment, cria um chatId e escuta as mensagens não lidas
    appointments.forEach((appointment) => {
      const chatId = `${appointment.terapeutaId}-${user.uid}`;
      const chatRef = doc(db, 'chats', chatId);
      
      const unsubscribe = onSnapshot(chatRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const unreadCount = data.unreadCount?.[user.uid] || 0;
          setUnreadMessages(prev => ({
            ...prev,
            [chatId]: unreadCount
          }));
        }
      });
      
      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [user?.uid, appointments]);

  useEffect(() => {
    if (!user?.uid) {
      setUserParticipations(new Set());
      return;
    }
    const participationsQuery = query(collectionGroup(db, "participants"), where("userId", "==", user.uid));
    const unsubscribeParticipations = onSnapshot(participationsQuery, async (snapshot) => {
      const participatingSessionIds = new Set<string>();
      for (const participantDoc of snapshot.docs) {
        const data = participantDoc.data();
        if (data.appointmentId) {
          try {
            const appointmentDocRef = doc(db, "appointments", data.appointmentId);
            const appointmentSnap = await getDoc(appointmentDocRef);
            if (appointmentSnap.exists()) {
              const appointmentData = appointmentSnap.data() as any;
              const sessionTime = appointmentData?.dataSessao?.toDate ? appointmentData.dataSessao.toDate() : new Date();
              if (getSessionEndTime(sessionTime) > new Date()) {
                participatingSessionIds.add(data.appointmentId);
              }
            }
          } catch (error) {
            console.warn(
              `Não foi possível verificar a participação na sessão ${data.appointmentId} por falta de permissão. A sessão pode ter se tornado privada.`
            );
          }
        }
      }
      setUserParticipations(participatingSessionIds);
    });
    return () => unsubscribeParticipations();
  }, [user?.uid]);

  // Buscar detalhes dos appointments onde o usuário está participando
  useEffect(() => {
    if (!user?.uid || scheduledParticipationsSet.size === 0) {
      setScheduledParticipations([]);
      return;
    }

    const fetchParticipationDetails = async () => {
      const participationIds = Array.from(scheduledParticipationsSet);
      const participationsData: Appointment[] = [];

      for (const appointmentId of participationIds) {
        try {
          const appointmentDoc = await getDocs(
            query(collection(db, "appointments"), where("__name__", "==", appointmentId))
          );
          
          if (!appointmentDoc.empty) {
            const data = appointmentDoc.docs[0].data();
            const appointmentData: Appointment = {
              id: appointmentId,
              appointmentId: appointmentId,
              pacienteId: data.pacienteId || '',
              terapeutaId: data.terapeutaId || '',
              pacienteNome: data.pacienteNome || 'Paciente',
              terapeutaNome: data.terapeutaNome || 'Terapeuta',
              dataSessao: data.dataSessao,
              status: data.status || 'confirmado',
              ...data,
            };
            participationsData.push(appointmentData);
          }
        } catch (error) {
          console.warn(`Erro ao buscar detalhes da participação ${appointmentId}:`, error);
        }
      }

      // Ordenar por data
      participationsData.sort((a, b) => 
        safeGetDate(a.dataSessao).getTime() - safeGetDate(b.dataSessao).getTime()
      );

      setScheduledParticipations(participationsData);
    };

    fetchParticipationDetails();
  }, [user?.uid, scheduledParticipationsSet]);

  // ======================= INÍCIO DA CORREÇÃO =======================
  useEffect(() => {
    if (!user?.uid) {
      setPublicSessions([]);
      // Define o carregamento como falso mesmo se o usuário não puder participar
      setIsLoading(false);
      return;
    }

    // A consulta ao Firestore agora é mais simples e mais fiável.
    // Ela busca todas as sessões que estão confirmadas e permitem participantes.
    const publicSessionsQuery = query(
      collection(db, "appointments"),
      where("permiteParticipantes", "==", true),
      where("status", "==", "confirmado")
    );

    const unsubscribePublicSessions = onSnapshot(
      publicSessionsQuery,
      (snapshot) => {
        const thirtyMinutesAgo = new Date();
        thirtyMinutesAgo.setMinutes(thirtyMinutesAgo.getMinutes() - 30);

        // A lógica de filtragem agora acontece aqui, no cliente.
        const sessionsList: Appointment[] = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() } as Appointment))
          .filter((session) => {
            // Garante que a sessão não é do próprio usuário E que a data é válida.
            const sessionTime = safeGetDate(session.dataSessao);
            return session.pacienteId !== user.uid && sessionTime > thirtyMinutesAgo;
          });

        // Ordena a lista já filtrada.
        sessionsList.sort((a, b) => safeGetDate(a.dataSessao).getTime() - safeGetDate(b.dataSessao).getTime());
        setPublicSessions(sessionsList);
      },
      (error) => {
        console.error("Erro ao buscar sessões públicas:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribePublicSessions();
  }, [user?.uid, canParticipateInSessions]);
  // ======================== FIM DA CORREÇÃO =========================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="ml-2">Carregando...</span>
      </div>
    );
  }

  // O restante do JSX do componente permanece o mesmo...
  return (
    <div className="space-y-6 relative">
      <div className="flex items-center justify-between mt-3">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard do Paciente</h1>
        <button
          onClick={handleScheduleConstellation}
          className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 font-medium shadow-md">
          <Plus className="w-5 h-5" /> Agendar Constelação
        </button>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto">
          {[
            { id: "sessions", label: "Minhas Sessões", icon: Calendar },
            { id: "subscriptions", label: "Assinaturas", icon: CreditCard },
            { id: "credits", label: "Meus Créditos", icon: Star },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}>
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        {activeTab === "sessions" && (
          <div className="p-6 space-y-6">
            {/* Salas Privadas (Você foi convidado) */}
            {invitedRooms.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Salas Privadas (Você foi convidado)
                </h2>
                <p className="text-sm text-gray-600 mb-4">Você foi convidado para participar destas salas privadas de terapeutas.</p>
                <div className="space-y-4">
                  {invitedRooms.map((room) => (
                    <div
                      key={room.id}
                      className={`border rounded-lg p-4 transition-colors ${
                        room.isActive ? "border-green-300 bg-green-50" : "border-gray-200 hover:bg-gray-50"
                      }`}>
                      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-600" />
                            {room.name || `Sala de ${room.createdByName}`}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Terapeuta: {room.createdByName}
                          </p>
                          <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Criada em:{" "}
                            {room.createdAt.toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {room.isActive ? (
                              <span className="text-xs text-green-700 font-medium bg-green-100 px-2 py-1 rounded">Sala Ativa</span>
                            ) : (
                              <span className="text-xs text-gray-700 font-medium bg-gray-100 px-2 py-1 rounded">Sala Inativa</span>
                            )}
                            <span className="text-xs text-blue-700 font-medium bg-blue-100 px-2 py-1 rounded">Convite Privado</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 md:self-start">
                          {room.isActive ? (
                            <button
                              onClick={() => navigate(`/sala/${room.id}`, {
                                state: {
                                  from: `/sala/${room.id}`,
                                  roomId: room.id,
                                  isInvitedRoom: true,
                                },
                              })}
                              className="w-full md:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                              <Video className="w-4 h-4" />
                              Entrar na Sala
                            </button>
                          ) : (
                            <span className="w-full md:w-auto px-4 py-2 bg-gray-300 text-gray-600 rounded-lg text-sm text-center">Sala Inativa</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Suas Consultas Agendadas */}
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" /> Suas Consultas Agendadas
              </h2>
              {appointments.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-500 mb-2">Nenhuma sessão agendada</h3>
                  <p className="text-gray-400 mb-6">Suas próximas consultas aparecerão aqui.</p>
                  <button
                    onClick={handleScheduleConstellation}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center gap-2">
                    <Plus className="w-5 h-5" /> Agendar Constelação
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {appointments.map((appointment) => {
                    const sessionTime = safeGetDate(appointment.dataSessao);
                    const now = new Date();
                    const minutesDiff = (sessionTime.getTime() - now.getTime()) / 60000;

                    // Obtém a duração da sessão do terapeuta (em minutos)
                    const sessionDuration = therapistSessionDurations[appointment.terapeutaId] || 60;

                    // Calcula o horário de término da sessão
                    const sessionEndTime = new Date(sessionTime.getTime() + sessionDuration * 60000);
                    const hasSessionEnded = now > sessionEndTime;

                    // Botão aparece 30 minutos antes e some após o término da duração
                    const canJoin = minutesDiff <= 30 && !hasSessionEnded;
                    const isActive = minutesDiff <= 0 && !hasSessionEnded;

                    // Primeiro agendamento tem destaque especial
                    const isNextAppointment = appointment.isNextSession;
                    const chatId = `${appointment.terapeutaId}-${user?.uid}`;
                    const unreadCount = unreadMessages[chatId] || 0;

                    return (
                      <div
                        key={appointment.id}
                        className={`border rounded-lg p-4 transition-all ${
                          isNextAppointment
                            ? "border-blue-400 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg ring-2 ring-blue-200"
                            : isActive
                            ? "border-green-300 bg-green-50"
                            : "border-gray-200"
                        }`}>
                        {isNextAppointment && (
                          <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-blue-200">
                            <div className="flex gap-1">
                              <Star className="w-5 h-5 text-blue-600 fill-blue-600" />
                              <span className="text-sm font-semibold text-blue-700">Próxima Sessão</span>
                            </div>

                            {isAppointmentConfirmed(appointment.status) && (
                              <ChatButton
                                label="Falar com Terapeuta"
                                unreadCount={unreadCount}
                                onClick={() =>
                                  setOpenChat({
                                    therapistId: appointment.terapeutaId,
                                    therapistName: appointment.terapeutaNome,
                                    otherMemberId: user?.uid || "",
                                    otherMemberName: user?.name || user?.email || "Você",
                                    otherMemberRole: ParticipantRole.CONSTELADO,
                                  })
                                }
                              />
                            )}
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className={`font-medium ${isNextAppointment ? "text-lg text-gray-900" : "text-gray-900"}`}>
                              {appointment.terapeutaNome}
                            </h3>
                            <p className={`text-sm text-gray-600 mt-1 ${isNextAppointment ? "font-medium" : ""}`}>
                              {sessionTime.toLocaleString("pt-BR", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>

                            {isActive && (
                              <p className="text-sm text-green-700 font-medium mt-1 flex items-center gap-1">
                                <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
                                Sessão em andamento
                              </p>
                            )}

                            {canJoin && !isActive && isAppointmentConfirmed(appointment.status) && (
                              <p className="text-sm text-blue-700 font-medium mt-1">
                                Disponível para entrar em {Math.floor(minutesDiff)} minutos
                              </p>
                            )}

                            {hasSessionEnded && <p className="text-sm text-gray-500 mt-1">Sessão encerrada</p>}
                          </div>

                          <div className="flex items-center gap-3">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                isAppointmentConfirmed(appointment.status)
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}>
                              {appointment.status}
                            </span>

                            {canJoin && isAppointmentConfirmed(appointment.status) && (
                              <button
                                onClick={() => navigate(`/sala/sala-${appointment.terapeutaId}-${appointment.id}`, {
                                  state: { appointmentId: appointment.id, terapeutaId: appointment.terapeutaId },
                                })}
                                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 shadow-md ${
                                  isActive
                                    ? "bg-green-600 hover:bg-green-700 text-white ring-2 ring-green-300 hover:shadow-lg"
                                    : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg"
                                }`}>
                                <Video className="w-4 h-4" />
                                {isActive ? "Entrar Agora" : "Entrar na Sessão"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Participações Agendadas */}
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Participações Agendadas
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Sessões onde você confirmou participação como observador.
              </p>
              {scheduledParticipations.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-4">Você não está agendado para participar de nenhuma sessão.</p>
                  <button
                    onClick={() => navigate("/quero-participar")}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors inline-flex items-center gap-2">
                    <Users className="w-4 h-4" /> Ver Sessões Disponíveis
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {scheduledParticipations.map((participation) => {
                    const sessionDate = safeGetDate(participation.dataSessao);
                    const now = new Date();
                    const minutesDiff = (sessionDate.getTime() - now.getTime()) / 60000;
                    const sessionDuration = therapistSessionDurations[participation.terapeutaId] || 60;
                    const canJoinNow = minutesDiff <= 10 && minutesDiff >= -sessionDuration;
                    const isActive = minutesDiff <= 0 && minutesDiff >= -sessionDuration;
                    const isAlreadyInRoom = isActive && userParticipations.has(participation.appointmentId || participation.id);
                    const chatId = `${participation.terapeutaId}-${user?.uid}`;

                    return (
                      <div
                        key={participation.appointmentId || participation.id}
                        className={`border rounded-lg p-4 transition-colors ${
                          isActive ? "border-green-300 bg-green-50" : "border-blue-200 bg-blue-50"
                        }`}>
                        <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
                          <div className="flex-1">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-2 mb-3 pb-3 border-b border-blue-200">
                              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                                <Users className="w-4 h-4 text-blue-600" />
                                {participation.tipo === "constelacao" ? "Constelação" : "Sessão"} com {participation.terapeutaNome}
                              </h3>
                              <div className="flex flex-col md:flex-row gap-3">
                                <div className="flex items-center gap-3">
                                  {canJoinNow ? (
                                    <button
                                      onClick={() => navigate(`/sala/sala-${participation.terapeutaId}-${participation.appointmentId || participation.id}`, {
                                        state: {
                                          role: "participant",
                                          appointmentId: participation.appointmentId || participation.id,
                                          participantType: hasActiveClubeAccess ? "clube" : "representante",
                                        },
                                      })}
                                      className="px-4 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2">
                                      <Video className="w-4 h-4" />
                                      {isAlreadyInRoom ? "Voltar para Sala" : isActive ? "Entrar Agora" : "Entrar"}
                                    </button>
                                  ) : minutesDiff > 10 ? (
                                    <span className="px-4 py-1 bg-gray-100 text-gray-600 rounded-lg text-sm">
                                      Abre em {Math.floor(minutesDiff / 60)}h {Math.floor(minutesDiff % 60)}min
                                    </span>
                                  ) : (
                                    <span className="px-4 py-1 bg-gray-300 text-gray-600 rounded-lg text-sm">Sessão encerrada</span>
                                  )}
                                </div>
                                <ChatButton 
                                  label="Falar com Terapeuta" 
                                  unreadCount={unreadMessages[chatId] || 0}
                                  onClick={() => setOpenChat({
                                    therapistId: participation.terapeutaId,
                                    therapistName: participation.terapeutaNome,
                                    otherMemberId: user?.uid || '',
                                    otherMemberName: user?.name || user?.email || 'Você',
                                    otherMemberRole: ParticipantRole.OBSERVADOR,
                                  })}
                                />
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              {sessionDate.toLocaleString("pt-BR", {
                                weekday: "long",
                                day: "2-digit",
                                month: "long",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-blue-700 font-medium bg-blue-100 px-2 py-1 rounded">
                                Participação Agendada
                              </span>
                              {isActive && (
                                <span className="text-xs text-green-700 font-medium bg-green-100 px-2 py-1 rounded">
                                  Em andamento agora
                                </span>
                              )}
                              {canJoinNow && !isActive && (
                                <span className="text-xs text-green-700 font-medium bg-green-100 px-2 py-1 rounded">
                                  Disponível para entrar
                                </span>
                              )}
                              {isAlreadyInRoom && (
                                <span className="text-xs text-purple-700 font-medium bg-purple-100 px-2 py-1 rounded">
                                  Você entrou
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Aba de Assinaturas */}
        {activeTab === "subscriptions" && (
          <div className="p-6">
            <ActiveSubscriptionsPanel />
          </div>
        )}

        {/* Aba de Créditos */}
        {activeTab === "credits" && (
          <div className="p-6 space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-600" />
              Meus Créditos Disponíveis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Créditos de Constelação */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-purple-900">Sessões de Constelação</h3>
                  <Star className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-purple-700 mb-2">
                  {creditBalances.sessions.constellation}
                </p>
                <p className="text-xs text-purple-700">
                  Créditos disponíveis para agendar suas próprias constelações
                </p>
                <button
                  onClick={() => navigate("/planos?secao=paciente")}
                  className="mt-3 w-full bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium">
                  Adquirir mais créditos
                </button>
              </div>

              {/* Créditos de Participação */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-green-900">Participação em Sessões</h3>
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-green-700 mb-2">
                  {creditBalances.sessions.participation}
                </p>
                <p className="text-xs text-green-700">
                  Créditos para participar como observador em sessões de outros
                </p>
                <button
                  onClick={() => navigate("/planos?secao=paciente")}
                  className="mt-3 w-full bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium">
                  Adquirir mais créditos
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">Como funcionam os créditos?</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>Créditos de Constelação:</strong> Use para agendar suas próprias sessões de constelação</li>
                    <li>• <strong>Créditos de Participação:</strong> Use para participar como observador/representante em sessões de outros</li>
                    <li>• Créditos são adicionados automaticamente ao adquirir um plano</li>
                    <li>• Créditos de assinaturas mensais são renovados todo mês</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal do Chat */}
      {openChat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl lg:h-[600px]">
            <button
              onClick={() => setOpenChat(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              aria-label="Fechar chat"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <Chat 
              therapistId={openChat.therapistId}
              therapistName={openChat.therapistName}
              otherMemberId={openChat.otherMemberId}
              otherMemberName={openChat.otherMemberName}
              otherMemberRole={openChat.otherMemberRole}
              currentUserId={user?.uid || ''}
              currentUserName={user?.name || user?.email || 'Você'}
              currentUserRole={user?.uid === openChat.therapistId ? ParticipantRole.TERAPEUTA : openChat.otherMemberRole}
            />
          </div>
        </div>
      )}

      {/* Widget de Chat Flutuante */}
      <ChatWidget 
        currentUserId={user?.uid || ''}
        currentUserName={user?.name || user?.email || 'Você'}
        currentUserRole={ParticipantRole.CONSTELADO}
        isTherapist={false}
      />
    </div>
  );
};

// Componente Principal: Dashboard Page
const DashboardPage: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Carregando painel...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Acesso Negado</h2>
          <p className="text-gray-600">Você precisa estar logado para acessar esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F6F1]">
      {/* Header fixo */}
      <AppHeader />

      {/* Content with padding for fixed header */}
      <div className="pt-16 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Renderização condicional baseada no role */}
          {user.role === "administrador" && <AdminDashboard />}
          {user.role === "terapeuta" && <TherapistDashboard />}
          {(user.role === "paciente" || user.role === "participante") && <PatientDashboard />}

          {/* Fallback para roles não reconhecidos */}
          {!["administrador", "terapeuta", "paciente", "participante"].includes(user.role) && (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Perfil Incompleto</h2>
              <p className="text-gray-600 mb-4">Seu perfil ainda não foi configurado completamente.</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                Atualizar Página
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
